import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cancelBookingCheckoutDocument, cancelBookingReversalDocument, createBookingAccountService, deleteBooking, deleteBookingAccountService, getBookingAccounts, getBookingDocumentNumberPreview, getCatalogItems, getCreditCardCircuits, getCustomers, getFiscalDocumentTypes, getRooms, getServiceCatalog, getSites, issueBookingCheckoutDocument, issueBookingReversalDocument, updateBooking, updateBookingAccountService } from "../api";
import VestaSelect from "../components/common/VestaSelect";
import DefaultNoteTextarea from "../components/common/DefaultNoteTextarea";
import { OverlayFieldError, OverlayFieldLabel } from "../components/common/OverlayValidation";
import VestaDateInput from "../components/common/VestaDateInput";
import { BookingOverlay, BookingPaymentsEditor, bookingToDraft, normalizeBookingPayments, notifyFiscalNumberingRefresh, toCheckInDateTimeFromInput, toCheckOutDateTimeFromInput, valueToDateTimeInput } from "../components/bookings/bookingPlanningParts";
import BookingStayTabs from "../components/bookings/BookingStayTabs";
import vestaLogo from "../assets/images/svg/logo_img.png";
import { createDocxImagePagesBlob } from "../utils/officeExport";
import { createFiscalVisualExportBlob, printFiscalPreviewRoot } from "../utils/fiscalDocumentExport";
import ManagementFilterHeader, { formatManagementSearchInput, ManagementListLoading, makeManagementFilterOptions, matchesManagementFilter, sortManagementRows } from "../components/management/ManagementFilterHeader";
import useIncrementalRows from "../hooks/useIncrementalRows";
import {
  UtilityActionIconButton,
  UtilityExportOverlay,
  UtilityFullscreenPreview,
  downloadBlob,
  getCompanyDisplayName,
  slugify,
} from "./UtilityWindows";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/bookingAccounts.css";
import "../assets/css/utility.css";

const STATUS_LABELS = {
  PENDING: "In attesa",
  CONFIRMED: "Confermata",
  CHECKED_IN: "In house",
  CHECKED_OUT: "Conclusa",
  CANCELLED: "Cancellata",
};

const CHARGE_TYPE_LABELS = {
  FORFAIT: "Forfait",
  DAILY: "Giornaliero",
  HOURLY: "Orario",
  ACCUMULO: "Accumulo",
};

const SERVICE_SOURCE_OPTIONS = [
  { value: "AUTOMATIC", label: "Automatico" },
  { value: "MANUAL_DESKTOP", label: "Manuale (da Desktop)" },
  { value: "MANUAL_MOBILE", label: "Manuale (da Mobile)" },
  { value: "DEFAULT", label: "Default" },
];

const EMPTY_SERVICE_DRAFT = {
  service_id: "",
  description: "",
  chargeType: "FORFAIT",
  entrySource: "MANUAL_DESKTOP",
  accumulationItems: [],
  quantity: 1,
  unitPrice: 0,
  discountRate: 0,
  vatRate: 0,
  chargeDate: "",
  note: "",
  isNew: false,
};

export const EMPTY_CHECKOUT_DRAFT = {
  documentId: "",
  documentType: "INVOICE",
  numberingMode: "AUTO",
  documentNumber: "",
  emissionDate: "",
  orientation: "PORTRAIT",
  unconditionalDiscountAmount: 0,
  unconditionalDiscountRate: 0,
  targetGrossTotal: "",
  roundingAmount: 0,
  payments: [],
  note: "",
  reversalDocumentId: "",
  reversalDocumentType: "CREDIT_NOTE",
  reversalNumberingMode: "AUTO",
  reversalDocumentNumber: "",
  reversalEmissionDate: "",
  reversalOrientation: "PORTRAIT",
  reversalNote: "",
};

const EMPTY_COLUMN_FILTERS = {
  room: [],
  guest: [],
  checkIn: [],
  checkOut: [],
  services: [],
  bookingTotal: [],
  servicesTotal: [],
  invoiceTotal: [],
};

const DOCUMENT_TYPE_LABELS = {
  RECEIPT: "Ricevuta",
  INVOICE: "Fattura",
  CREDIT_NOTE: "Nota di Credito",
  RECEIPT_REVERSAL: "Storno Ricevuta",
};


const EMPTY_EXPORT_IMAGE_DATA_URL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

function nextPaint() {
  return new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
}

function waitForImage(image) {
  if (!image || image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    image.addEventListener("load", resolve, { once: true });
    image.addEventListener("error", resolve, { once: true });
  });
}

function safeComputedExportValue(value) {
  const raw = String(value || "");
  // Le immagini del report sono gestite separatamente come <img> e convertite in data URL.
  // Qualunque URL CSS (font-image, mask, cursor, filtri, ecc.) viene neutralizzato:
  // non serve al layout fiscale e non può più contaminare il canvas.
  return /url\(/i.test(raw) ? "none" : raw;
}

async function inlineComputedFiscalStyles(sourceRoot, cloneRoot) {
  const sourceElements = [sourceRoot, ...Array.from(sourceRoot.querySelectorAll("*"))];
  const cloneElements = [cloneRoot, ...Array.from(cloneRoot.querySelectorAll("*"))];

  for (let index = 0; index < cloneElements.length; index += 1) {
    const sourceElement = sourceElements[index];
    const cloneElement = cloneElements[index];
    if (!sourceElement || !cloneElement || !(cloneElement instanceof Element)) continue;
    const computed = window.getComputedStyle(sourceElement);
    const declarations = [];
    for (const property of Array.from(computed || [])) {
      const value = safeComputedExportValue(computed.getPropertyValue(property));
      if (!value) continue;
      const priority = computed.getPropertyPriority(property);
      declarations.push(`${property}:${value}${priority ? " !important" : ""}`);
    }
    cloneElement.setAttribute("style", declarations.join(";"));
  }
}

async function safeRasterBlobToDataUrl(blob) {
  if (!blob) return EMPTY_EXPORT_IMAGE_DATA_URL;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let mime = "";
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) mime = "image/png";
  else if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) mime = "image/jpeg";
  else if (bytes.length >= 6 && String.fromCharCode(...bytes.slice(0, 6)).startsWith("GIF8")) mime = "image/gif";
  else if (bytes.length >= 12 && String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" && String.fromCharCode(...bytes.slice(8, 12)) === "WEBP") mime = "image/webp";
  if (!mime) return EMPTY_EXPORT_IMAGE_DATA_URL;
  return blobToDataUrl(new Blob([bytes], { type: mime }));
}

async function loadedImageToDataUrl(image) {
  const source = image?.currentSrc || image?.src || "";
  if (!source) return EMPTY_EXPORT_IMAGE_DATA_URL;
  if (source.startsWith("data:")) return /^data:image\/(?:png|jpeg|jpg|gif|webp);/i.test(source) ? source : EMPTY_EXPORT_IMAGE_DATA_URL;

  try {
    const absoluteUrl = source.startsWith("blob:") ? source : new URL(source, document.baseURI).href;
    let credentials = "omit";
    if (!absoluteUrl.startsWith("blob:")) {
      try {
        credentials = new URL(absoluteUrl).origin === window.location.origin ? "same-origin" : "omit";
      } catch (_urlError) {
        credentials = "omit";
      }
    }
    const response = await fetch(absoluteUrl, { credentials, cache: "no-store" });
    if (response.ok) return await safeRasterBlobToDataUrl(await response.blob());
  } catch (_fetchError) {
    // Se il server del logo non espone CORS, nell'export non lasciamo mai l'URL remoto.
  }

  return EMPTY_EXPORT_IMAGE_DATA_URL;
}

async function sanitizeFiscalCloneResources(sourcePage, clone) {
  const sourceImages = Array.from(sourcePage.querySelectorAll("img"));
  const cloneImages = Array.from(clone.querySelectorAll("img"));
  for (let index = 0; index < cloneImages.length; index += 1) {
    const cloneImage = cloneImages[index];
    const sourceImage = sourceImages[index];
    const dataUrl = sourceImage ? await loadedImageToDataUrl(sourceImage) : EMPTY_EXPORT_IMAGE_DATA_URL;
    cloneImage.removeAttribute("srcset");
    cloneImage.removeAttribute("sizes");
    cloneImage.removeAttribute("crossorigin");
    cloneImage.removeAttribute("referrerpolicy");
    cloneImage.setAttribute("src", dataUrl || EMPTY_EXPORT_IMAGE_DATA_URL);
  }

  clone.querySelectorAll("source").forEach((source) => {
    source.removeAttribute("srcset");
    source.removeAttribute("sizes");
    source.removeAttribute("src");
  });

  clone.querySelectorAll("iframe, object, embed, video, audio").forEach((element) => element.remove());

  for (const svgImage of Array.from(clone.querySelectorAll("svg image, svg feImage"))) {
    const href = svgImage.getAttribute("href") || svgImage.getAttribute("xlink:href") || "";
    const isSafeRasterData = /^data:image\/(?:png|jpeg|jpg|gif|webp);/i.test(href);
    svgImage.setAttribute("href", isSafeRasterData ? href : EMPTY_EXPORT_IMAGE_DATA_URL);
    svgImage.removeAttribute("xlink:href");
  }

  for (const svgUse of Array.from(clone.querySelectorAll("svg use"))) {
    const href = svgUse.getAttribute("href") || svgUse.getAttribute("xlink:href") || "";
    if (href && !href.startsWith("#")) {
      svgUse.removeAttribute("href");
      svgUse.removeAttribute("xlink:href");
    }
  }

  for (const element of Array.from(clone.querySelectorAll("[style]"))) {
    const inlineStyle = element.getAttribute("style") || "";
    if (/url\(/i.test(inlineStyle)) element.setAttribute("style", inlineStyle.replace(/url\([^)]*\)/gi, "none"));
  }
}

function canvasToJpegBlob(canvas) {
  return new Promise((resolve, reject) => {
    try {
      // Forza il controllo origin-clean prima di arrivare a toBlob: se qualcosa è rimasto remoto
      // otteniamo un errore gestibile e possiamo effettuare il secondo passaggio senza risorse URL.
      canvas.getContext("2d")?.getImageData(0, 0, 1, 1);
      canvas.toBlob((value) => value ? resolve(value) : reject(new Error("Impossibile creare l'immagine del documento")), "image/jpeg", 0.98);
    } catch (error) {
      reject(error);
    }
  });
}

async function renderFiscalCloneToCanvas(clone, width, height, scale) {
  const serialized = new XMLSerializer().serializeToString(clone);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><foreignObject x="0" y="0" width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml">${serialized}</div></foreignObject></svg>`;
  const svgUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  const image = new Image();
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error("Impossibile preparare il documento per l'esportazione"));
      image.src = svgUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
    if (!context) throw new Error("Canvas non disponibile");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

function stripAllUrlResources(clone) {
  clone.querySelectorAll("img").forEach((image) => {
    image.removeAttribute("srcset");
    image.setAttribute("src", EMPTY_EXPORT_IMAGE_DATA_URL);
  });
  clone.querySelectorAll("svg image, svg feImage, svg use").forEach((element) => {
    element.removeAttribute("href");
    element.removeAttribute("xlink:href");
  });
  clone.querySelectorAll("[style]").forEach((element) => {
    const style = element.getAttribute("style") || "";
    element.setAttribute("style", style.replace(/url\([^)]*\)/gi, "none"));
  });
}

async function rasterizeFiscalPage(page, scale = 2) {
  const rect = page.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  const clone = page.cloneNode(true);

  // Copiamo gli stili calcolati del solo report. Non serializziamo più tutti i fogli CSS
  // dell'app: regole, font o immagini estranee al documento erano la fonte del canvas tainted.
  await inlineComputedFiscalStyles(page, clone);
  await sanitizeFiscalCloneResources(page, clone);

  try {
    const canvas = await renderFiscalCloneToCanvas(clone, width, height, scale);
    return { blob: await canvasToJpegBlob(canvas), width: canvas.width, height: canvas.height };
  } catch (error) {
    if (error?.name !== "SecurityError" && !/taint|origin-clean|insecure/i.test(String(error?.message || ""))) throw error;

    // Fallback di sicurezza: nessun URL esterno sopravvive. L'export deve funzionare anche
    // con backend/CDN che non espongono correttamente gli header CORS in locale o online.
    const safeClone = clone.cloneNode(true);
    stripAllUrlResources(safeClone);
    const canvas = await renderFiscalCloneToCanvas(safeClone, width, height, scale);
    return { blob: await canvasToJpegBlob(canvas), width: canvas.width, height: canvas.height };
  }
}

async function createFiscalSnapshots(printRoot) {
  const sourcePages = Array.from(printRoot?.children || []);
  if (!sourcePages.length) return [];
  const host = document.createElement("div");
  host.className = "app_root vesta_fiscal_export_capture";
  sourcePages.forEach((page) => host.appendChild(page.cloneNode(true)));
  document.body.appendChild(host);
  try {
    await nextPaint();
    await Promise.all(Array.from(host.querySelectorAll("img")).map(waitForImage));
    if (document.fonts?.ready) await document.fonts.ready;
    await nextPaint();
    const pages = Array.from(host.children);
    const snapshots = [];
    for (const page of pages) snapshots.push(await rasterizeFiscalPage(page));
    return snapshots;
  } finally {
    host.remove();
  }
}

function concatBytes(...chunks) {
  const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(length);
  let offset = 0;
  chunks.forEach((chunk) => {
    result.set(chunk, offset);
    offset += chunk.length;
  });
  return result;
}

async function createSnapshotPdfBlob(snapshots, orientation) {
  const encoder = new TextEncoder();
  const landscape = orientation === "landscape";
  const pageWidth = landscape ? 841.89 : 595.28;
  const pageHeight = landscape ? 595.28 : 841.89;
  const objectCount = 2 + snapshots.length * 3;
  const objects = new Array(objectCount + 1);
  const pageIds = [];

  for (let index = 0; index < snapshots.length; index += 1) {
    const pageId = 3 + index * 3;
    const contentId = pageId + 1;
    const imageId = pageId + 2;
    pageIds.push(pageId);
    const jpegBytes = new Uint8Array(await snapshots[index].blob.arrayBuffer());
    const imageHeader = encoder.encode(`<< /Type /XObject /Subtype /Image /Width ${snapshots[index].width} /Height ${snapshots[index].height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`);
    const imageFooter = encoder.encode("\nendstream");
    objects[imageId] = concatBytes(imageHeader, jpegBytes, imageFooter);
    const content = `q\n${pageWidth.toFixed(2)} 0 0 ${pageHeight.toFixed(2)} 0 0 cm\n/Im${index + 1} Do\nQ`;
    objects[contentId] = encoder.encode(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    objects[pageId] = encoder.encode(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth.toFixed(2)} ${pageHeight.toFixed(2)}] /Resources << /XObject << /Im${index + 1} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`);
  }

  objects[1] = encoder.encode("<< /Type /Catalog /Pages 2 0 R >>");
  objects[2] = encoder.encode(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);

  const chunks = [];
  const header = encoder.encode("%PDF-1.4\n%VestaMNG\n");
  chunks.push(header);
  let offset = header.length;
  const offsets = new Array(objectCount + 1).fill(0);
  for (let id = 1; id <= objectCount; id += 1) {
    const start = encoder.encode(`${id} 0 obj\n`);
    const end = encoder.encode("\nendobj\n");
    offsets[id] = offset;
    chunks.push(start, objects[id], end);
    offset += start.length + objects[id].length + end.length;
  }
  const xrefOffset = offset;
  let xref = `xref\n0 ${objectCount + 1}\n0000000000 65535 f \n`;
  for (let id = 1; id <= objectCount; id += 1) xref += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  xref += `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  chunks.push(encoder.encode(xref));
  return new Blob(chunks, { type: "application/pdf" });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Impossibile leggere il file esportato"));
    reader.readAsDataURL(blob);
  });
}

export function todayIso() {
  const parts = new Intl.DateTimeFormat("it-IT", {
    timeZone: "Europe/Rome",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const read = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${read("year")}-${read("month")}-${read("day")}`;
}

export function buildCheckoutDraft(booking = {}) {
  const stayClosed = String(booking?.status || "").toUpperCase() === "CHECKED_OUT";
  const existingDocument = stayClosed ? booking?.latest_fiscal_document || null : null;
  const historicalDocument = !stayClosed && String(booking?.latest_checkout_history_document?.status || "").toUpperCase() === "CANCELLED"
    ? booking.latest_checkout_history_document
    : null;
  const formDocument = existingDocument || historicalDocument;
  const existingReversal = stayClosed ? booking?.latest_reversal_document || null : null;
  const historicalReversal = stayClosed && !existingReversal && String(booking?.latest_reversal_history_document?.status || "").toUpperCase() === "CANCELLED"
    ? booking.latest_reversal_history_document
    : null;
  const reversalFormDocument = existingReversal || historicalReversal;
  const defaultDocumentType = String(booking?.default_fiscal_document || "INVOICE").toUpperCase() === "RECEIPT" ? "RECEIPT" : "INVOICE";
  const defaultReversalType = String(booking?.default_reversal_document || "CREDIT_NOTE").toUpperCase() === "RECEIPT_REVERSAL" ? "RECEIPT_REVERSAL" : "CREDIT_NOTE";
  const existingIssueDate = formDocument?.issuedAt ? String(formDocument.issuedAt).slice(0, 10) : "";
  const reversalIssueDate = reversalFormDocument?.issuedAt ? String(reversalFormDocument.issuedAt).slice(0, 10) : "";
  return {
    ...EMPTY_CHECKOUT_DRAFT,
    documentId: existingDocument?._id || "",
    documentType: formDocument?.documentType || defaultDocumentType,
    numberingMode: historicalDocument ? "MANUAL" : (existingDocument?.numberingMode === "MANUAL" ? "MANUAL" : "AUTO"),
    documentNumber: String(formDocument?.documentSequence || formDocument?.documentNumber || "").replace(/\D/g, ""),
    emissionDate: existingIssueDate || todayIso(),
    orientation: "PORTRAIT",
    unconditionalDiscountAmount: Number(existingDocument?.unconditionalDiscountAmount || 0),
    unconditionalDiscountRate: Number(existingDocument?.unconditionalDiscountRate || 0),
    targetGrossTotal: existingDocument ? Number(existingDocument?.grossTotal || 0) : "",
    roundingAmount: Number(existingDocument?.roundingAmount || 0),
    payments: normalizeBookingPayments(booking.payments, booking.deposit_amount, booking.payment_method),
    note: formDocument?.note || "",
    reversalDocumentId: existingReversal?._id || "",
    reversalDocumentType: reversalFormDocument?.documentType || defaultReversalType,
    reversalNumberingMode: historicalReversal ? "MANUAL" : (existingReversal?.numberingMode === "MANUAL" ? "MANUAL" : "AUTO"),
    reversalDocumentNumber: String(reversalFormDocument?.documentSequence || reversalFormDocument?.documentNumber || "").replace(/\D/g, ""),
    reversalEmissionDate: reversalIssueDate || todayIso(),
    reversalOrientation: "PORTRAIT",
    reversalNote: reversalFormDocument?.note || "",
  };
}

export async function fetchFreshBookingAccount(booking = {}) {
  if (!booking?._id) throw new Error("Prenotazione non valida");
  const data = await getBookingAccounts({ bookingId: booking._id });
  const account = (data?.items || []).find((item) => String(item._id) === String(booking._id));
  if (!account) throw new Error("Dati check out non disponibili");
  return account;
}


function formatDate(value) {
  if (!value) return "-";
  const raw = String(value || "").trim();
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00` : valueToDateTimeInput(value);
  if (!normalized) return "-";
  const [year, month, day] = normalized.slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "-";
}

function formatDateTime(value) {
  const normalized = valueToDateTimeInput(value);
  if (!normalized) return "-";
  const [year, month, day] = normalized.slice(0, 10).split("-");
  return `${day}/${month}/${year} ${normalized.slice(11, 16)}`;
}

function formatMoney(value) {
  return `${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function stayNights(checkIn, checkOut) {
  const startValue = valueToDateTimeInput(checkIn);
  const endValue = valueToDateTimeInput(checkOut);
  if (!startValue || !endValue || new Date(endValue) <= new Date(startValue)) return 0;
  const startDay = Date.parse(`${startValue.slice(0, 10)}T00:00:00Z`);
  const endDay = Date.parse(`${endValue.slice(0, 10)}T00:00:00Z`);
  return Math.max(1, Math.round((endDay - startDay) / 86400000));
}

function bookingGuestLabel(item = {}) {
  return `Camera ${item.room_number || "-"} ${item.guest_name || ""} ${STATUS_LABELS[item.status] || item.status || ""}`.trim();
}

function bookingPeriodLabel(item = {}) {
  return `${formatDate(item.check_in)} - ${formatDate(item.check_out)}`;
}

function bookingServicesLabel(item = {}) {
  const rows = Array.isArray(item.services) ? item.services : [];
  return ["Soggiorno", ...rows.map((service) => service.description || "Servizio").filter(Boolean)].join(", ");
}

function bookingServiceDisplayRows(item = {}) {
  const rows = [{ key: "stay", description: "Soggiorno", grossAmount: Number(item.booking_total || 0) }];
  (Array.isArray(item.services) ? item.services : []).forEach((service) => {
    rows.push({ key: service._id || service.description, description: service.description || "Servizio", grossAmount: Number(service.grossAmount || 0) });
  });
  return rows;
}

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function checkoutRows(booking = {}) {
  const nights = Math.max(1, Number(stayNights(booking.check_in, booking.check_out) || booking.stay_nights || 1));
  const unitPrice = Number(booking.room_rate ?? (Number(booking.booking_total || 0) / nights));
  const taxableTotal = nights * unitPrice;
  const discountRate = Math.min(100, Math.max(0, Number(booking.room_discount_rate || 0)));
  const bookingNet = taxableTotal * (1 - discountRate / 100);
  const bookingVatRate = Math.min(100, Math.max(0, Number(booking.room_vat_rate || 0)));
  const bookingTaxAmount = bookingNet * bookingVatRate / 100;
  const rows = [
    {
      key: "booking",
      source: "Soggiorno",
      description: `Soggiorno camera ${booking.room_number || "-"} · Giornaliero`,
      quantity: nights,
      unitPrice,
      taxableTotal,
      discountRate,
      netTotal: bookingNet,
      vatRate: bookingVatRate,
      taxAmount: bookingTaxAmount,
      grossTotal: bookingNet + bookingTaxAmount,
    },
  ];

  (booking.services || []).forEach((service) => {
    const quantity = Number(service.quantity || 0);
    const unitPrice = Number(service.unitPrice || 0);
    const taxableTotal = Number.isFinite(Number(service.amount)) ? Number(service.amount) : quantity * unitPrice;
    const discountRate = Math.min(100, Math.max(0, Number(service.discountRate || 0)));
    const netTotal = Number.isFinite(Number(service.netAmount)) ? Number(service.netAmount) : taxableTotal * (1 - discountRate / 100);
    const vatRate = Math.min(100, Math.max(0, Number(service.vatRate || 0)));
    const taxAmount = Number.isFinite(Number(service.taxAmount)) ? Number(service.taxAmount) : netTotal * vatRate / 100;
    const grossTotal = Number.isFinite(Number(service.grossAmount)) ? Number(service.grossAmount) : netTotal + taxAmount;
    const chargeLabel = CHARGE_TYPE_LABELS[service.chargeType] || service.chargeType || "Forfait";
    rows.push({
      key: service._id || service.description,
      source: "Servizi Vari",
      description: `${service.description || "Servizio"} · ${chargeLabel}`,
      quantity,
      unitPrice,
      taxableTotal,
      discountRate,
      netTotal,
      vatRate,
      taxAmount,
      grossTotal,
    });
  });

  return rows;
}

function explicitVatRate(row = {}) {
  const raw = row.vatRate ?? row.ivaRate ?? row.taxRate ?? row.aliquotaIva;
  if (raw === "" || raw === null || raw === undefined) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function vatBreakdown(rows = []) {
  const grouped = new Map();
  let unconfiguredTaxable = 0;
  let unconfiguredTotal = 0;
  rows.forEach((row) => {
    const taxable = Number(row.taxableTotal || 0);
    const tax = Number(row.taxAmount || 0);
    const total = Number(row.grossTotal || 0);
    const rate = explicitVatRate(row);
    if (rate === null) {
      unconfiguredTaxable += taxable;
      unconfiguredTotal += total;
      return;
    }
    const key = String(rate);
    const current = grouped.get(key) || { rate, taxable: 0, vat: 0, total: 0 };
    current.taxable += taxable;
    current.vat += tax;
    current.total += total;
    grouped.set(key, current);
  });
  return {
    configured: [...grouped.values()].sort((a, b) => a.rate - b.rate),
    unconfiguredTaxable,
    unconfiguredTotal,
    complete: unconfiguredTotal <= 0.00001,
  };
}

function bookingPaymentSummary(booking = {}) {
  const payments = Array.isArray(booking.payments) ? booking.payments : [];
  const total = payments.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);
  const methods = [...new Set([
    ...payments.map((payment) => String(payment?.paymentMethod || "").trim()),
    String(booking.payment_method || "").trim(),
  ].filter(Boolean))];
  return {
    total,
    methods: methods.length ? methods.join(" · ") : "Non specificato",
  };
}

export function mergeServiceDraftIntoBookingAccount(booking = {}, draft = {}) {
  const stay = draft?.stay || {};
  const nights = Math.max(1, stayNights(booking.check_in, booking.check_out) || Number(stay.quantity || 1));
  const roomRate = Math.max(0, numberValue(stay.unitPrice, numberValue(booking.room_rate, 0)));
  const roomDiscountRate = Math.min(100, Math.max(0, numberValue(stay.discountRate, numberValue(booking.room_discount_rate, 0))));
  const roomVatRate = Math.min(100, Math.max(0, numberValue(stay.vatRate, numberValue(booking.room_vat_rate, 0))));
  const roomTaxable = nights * roomRate;
  const roomNet = roomTaxable * (1 - roomDiscountRate / 100);
  const roomTax = roomNet * roomVatRate / 100;
  const services = (Array.isArray(draft?.rows) ? draft.rows : []).map((row) => {
    const quantity = Math.max(0, numberValue(row.quantity, 0));
    const unitPrice = Math.max(0, numberValue(row.unitPrice, 0));
    const discountRate = Math.min(100, Math.max(0, numberValue(row.discountRate, 0)));
    const vatRate = Math.min(100, Math.max(0, numberValue(row.vatRate, 0)));
    const amount = quantity * unitPrice;
    const netAmount = amount * (1 - discountRate / 100);
    const taxAmount = netAmount * vatRate / 100;
    return { ...row, quantity, unitPrice, discountRate, vatRate, amount, netAmount, taxAmount, grossAmount: netAmount + taxAmount };
  });
  const servicesTaxable = services.reduce((sum, row) => sum + Number(row.netAmount || 0), 0);
  const servicesTax = services.reduce((sum, row) => sum + Number(row.taxAmount || 0), 0);
  return {
    ...booking,
    stay_nights: nights,
    room_rate: roomRate,
    room_discount_rate: roomDiscountRate,
    room_vat_rate: roomVatRate,
    room_charge_date: stay.chargeDate || booking.room_charge_date || booking.check_in,
    room_note: stay.note || "",
    room_net_total: roomNet,
    room_tax_amount: roomTax,
    total_amount: roomNet,
    booking_total: roomNet + roomTax,
    services,
    services_taxable_total: servicesTaxable,
    services_tax_total: servicesTax,
    services_total: servicesTaxable + servicesTax,
    invoice_taxable_total: roomNet + servicesTaxable,
    invoice_tax_total: roomTax + servicesTax,
    invoice_total: roomNet + roomTax + servicesTaxable + servicesTax,
  };
}

export function buildServiceDraftFromBooking(booking = {}) {
  const nights = Math.max(1, Number(stayNights(booking.check_in, booking.check_out) || booking.stay_nights || 1));
  return {
    stay: {
      chargeDate: booking.room_charge_date ? String(booking.room_charge_date).slice(0, 10) : String(booking.check_in || "").slice(0, 10),
      quantity: nights,
      unitPrice: Number(booking.room_rate ?? (Number((booking.room_net_total ?? booking.booking_total) || 0) / nights)),
      discountRate: Number(booking.room_discount_rate || 0),
      vatRate: Number(booking.room_vat_rate || booking.default_vat_rate || 0),
      entrySource: "AUTOMATIC",
      note: booking.room_note || "",
    },
    rows: (booking.services || []).map((service) => ({
      _id: service._id,
      service_id: service.service_id || "",
      description: service.description || "",
      chargeType: service.chargeType || "FORFAIT",
      entrySource: service.entrySource || "MANUAL_DESKTOP",
      accumulationItems: Array.isArray(service.accumulationItems) ? service.accumulationItems : [],
      quantity: Number(service.quantity || 1),
      unitPrice: Number(service.unitPrice || 0),
      discountRate: Number(service.discountRate || 0),
      vatRate: Number(service.vatRate || booking.default_vat_rate || 0),
      chargeDate: service.chargeDate ? String(service.chargeDate).slice(0, 10) : todayIso(),
      note: service.note || "",
      isNew: false,
    })),
    removedIds: [],
  };
}

function ServiceSourceSelect({ value }) {
  const label = SERVICE_SOURCE_OPTIONS.find((option) => option.value === (value || "MANUAL_DESKTOP"))?.label || value || "Manuale (da Desktop)";
  return <input value={label} disabled readOnly />;
}

function AccumulationDetailOverlay({ catalog, row, onSave, onClose }) {
  const [items, setItems] = useState(() => (Array.isArray(row?.accumulationItems) ? row.accumulationItems : []).map((item, index) => ({ ...item, _localId: item._localId || `acc-${index}-${Date.now()}` })));

  function patchItem(index, patch) {
    setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  }

  function chooseItem(index, serviceId) {
    const service = catalog.find((item) => String(item._id) === String(serviceId));
    patchItem(index, {
      service_id: serviceId,
      description: service?.description || "",
      quantity: 1,
      unitPrice: Number(service?.rate || 0),
    });
  }

  function addItem() {
    setItems((current) => [...current, { _localId: `acc-${Date.now()}-${current.length}`, service_id: "", description: "", quantity: 1, unitPrice: 0 }]);
  }

  const total = items.reduce((sum, item) => sum + numberValue(item.quantity, 1) * numberValue(item.unitPrice, 0), 0);

  return (
    <div className="overlay_backdrop booking_accumulation_backdrop">
      <div className="overlay_card booking_accumulation_overlay_card" role="dialog" aria-modal="true" aria-label="Dettaglio accumulo servizio">
        <div className="overlay_header">
          <h2>DETTAGLIO ACCUMULO</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="overlay_body booking_accumulation_overlay_body">
          <div className="overlay_section">
            <h3>Voci Accumulate</h3>
            <div className="overlay_list_editor">
              <div className="overlay_list_toolbar">
                <button type="button" className="overlay_list_add_button" onClick={addItem} aria-label="Aggiungi voce accumulo" title="Aggiungi voce accumulo">
                  <svg className="overlay_action_icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                </button>
                <p>Ogni voce contribuisce al prezzo totale del servizio principale.</p>
              </div>
              <div className="overlay_list_rows">
                {items.map((item, index) => {
                  const amount = numberValue(item.quantity, 1) * numberValue(item.unitPrice, 0);
                  return (
                    <div className="overlay_list_row booking_accumulation_row" key={item._localId || `${item.service_id}-${index}`}>
                      <label className="overlay_field"><span>Voce Servizio</span><VestaSelect value={item.service_id || ""} onChange={(event) => chooseItem(index, event.target.value)}><option value="">Seleziona servizio</option>{catalog.map((service) => <option key={service._id} value={service._id}>{service.description}</option>)}</VestaSelect></label>
                      <label className="overlay_field"><span>Quantità</span><input type="number" min="0.01" step="0.01" value={item.quantity ?? 1} onChange={(event) => patchItem(index, { quantity: event.target.value })} /></label>
                      <label className="overlay_field"><span>Prezzo</span><input type="number" min="0" step="0.01" value={item.unitPrice ?? 0} onChange={(event) => patchItem(index, { unitPrice: event.target.value })} /></label>
                      <label className="overlay_field"><span>Totale</span><input value={formatMoney(amount)} disabled readOnly /></label>
                      <button type="button" className="overlay_contact_remove_button overlay_list_remove_button" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label="Rimuovi voce accumulo" title="Rimuovi voce accumulo">×</button>
                    </div>
                  );
                })}
                {!items.length && <div className="overlay_list_empty">Nessuna voce accumulata.</div>}
              </div>
            </div>
          </div>
        </div>
        <div className="booking_accumulation_total"><span>Totale Accumulo</span><strong>{formatMoney(total)}</strong></div>
        <div className="overlay_footer">
          <div className="overlay_error_message" />
          <button type="button" className="overlay_save_button overlay_save_close_button" onClick={() => onSave(items)}>Salva e Chiudi</button>
        </div>
      </div>
    </div>
  );
}

export function BookingServiceOverlay({ booking, catalog, draft, error, onChange, onBookingDateChange, onClose, onSave, locked = false, activeStayTab = "services", onStayTabChange, stayTabErrors = {}, onStayTabErrorChange = null }) {
  const rows = Array.isArray(draft?.rows) ? draft.rows : [];
  const stay = draft?.stay || {};
  const [expandedRows, setExpandedRows] = useState({});
  const [accumulationIndex, setAccumulationIndex] = useState(null);

  useEffect(() => {
    if (!onStayTabChange || !error) return;
    onStayTabErrorChange?.("services", true);
  }, [error, onStayTabChange, onStayTabErrorChange]);

  function rowTotals(row = {}) {
    const taxable = numberValue(row.quantity, 1) * numberValue(row.unitPrice, 0);
    const discountRate = Math.min(100, Math.max(0, numberValue(row.discountRate, 0)));
    const net = taxable * (1 - discountRate / 100);
    const vatRate = Math.min(100, Math.max(0, numberValue(row.vatRate, 0)));
    const tax = net * vatRate / 100;
    return { taxable, discountRate, net, vatRate, tax, gross: net + tax };
  }

  function patchStay(patch) {
    onChange({ stay: { ...stay, ...patch } });
  }

  function patchRow(index, patch) {
    const nextRows = rows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const next = { ...row, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "chargeType") && patch.chargeType === "ACCUMULO") {
        next.quantity = 1;
        next.accumulationItems = Array.isArray(next.accumulationItems) ? next.accumulationItems : [];
        next.unitPrice = next.accumulationItems.reduce((sum, item) => sum + numberValue(item.quantity, 1) * numberValue(item.unitPrice, 0), 0);
      }
      return next;
    });
    onChange({ rows: nextRows });
  }

  function chooseService(index, serviceId) {
    const service = catalog.find((item) => item._id === serviceId);
    const chargeType = service?.defaultChargeType || "FORFAIT";
    patchRow(index, {
      service_id: serviceId,
      description: service?.description || "",
      chargeType,
      quantity: chargeType === "ACCUMULO" ? 1 : Number(service?.defaultQuantity || 1),
      unitPrice: chargeType === "ACCUMULO" ? 0 : Number(service?.rate || 0),
      discountRate: 0,
      vatRate: Number(service?.vatRate || booking?.default_vat_rate || 0),
      accumulationItems: [],
      note: service?.notes || "",
    });
  }

  function addServiceRow() {
    onChange({
      rows: [...rows, {
        ...EMPTY_SERVICE_DRAFT,
        vatRate: Number(booking?.default_vat_rate || 0),
        _localId: `new-${Date.now()}-${rows.length}`,
        isNew: true,
        entrySource: "MANUAL_DESKTOP",
        accumulationItems: [],
        chargeDate: todayIso(),
      }],
    });
  }

  function removeServiceRow(index) {
    const row = rows[index];
    const removedIds = Array.isArray(draft?.removedIds) ? draft.removedIds : [];
    onChange({
      rows: rows.filter((_, rowIndex) => rowIndex !== index),
      removedIds: row?._id ? [...new Set([...removedIds, row._id])] : removedIds,
    });
  }

  function saveAccumulation(items) {
    if (accumulationIndex === null) return;
    const normalizedItems = items.map((item) => ({
      service_id: item.service_id || "",
      description: item.description || catalog.find((service) => String(service._id) === String(item.service_id))?.description || "",
      quantity: numberValue(item.quantity, 1),
      unitPrice: numberValue(item.unitPrice, 0),
    })).filter((item) => item.service_id || item.description);
    const total = normalizedItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    patchRow(accumulationIndex, { accumulationItems: normalizedItems, unitPrice: total, quantity: 1 });
    setAccumulationIndex(null);
  }

  const stayComputed = rowTotals(stay);
  const totals = rows.reduce((acc, row) => {
    const computed = rowTotals(row);
    acc.taxable += computed.net;
    acc.tax += computed.tax;
    acc.gross += computed.gross;
    return acc;
  }, { taxable: stayComputed.net, tax: stayComputed.tax, gross: stayComputed.gross });
  const advanceTotal = normalizeBookingPayments(booking.payments, booking.deposit_amount, booking.payment_method)
    .filter((payment) => String(payment?.paymentType || "").toUpperCase() === "ADVANCE")
    .reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);
  const stayExpanded = Boolean(expandedRows.stay);

  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_account_overlay_card booking_services_overlay_card booking_stay_overlay_card" role="dialog" aria-modal="true" aria-label="Soggiorno">
        <div className="overlay_header">
          <h2>Soggiorno</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        {onStayTabChange && <BookingStayTabs activeTab={activeStayTab} onChange={onStayTabChange} checkoutDisabled={!(["CHECKED_IN", "CHECKED_OUT"].includes(String(booking.status || "").toUpperCase()))} errorTabs={stayTabErrors} />}

        <div className="overlay_body booking_services_overlay_body">
          <fieldset className="booking_stay_locked_fieldset" disabled={locked}>
          <div className="overlay_section booking_services_list_section">
            <h3>Servizi Addebitati</h3>
            <div className="overlay_list_editor booking_services_list_editor">
              <div className="overlay_list_toolbar booking_services_list_toolbar">
                <button type="button" className="overlay_list_add_button" onClick={addServiceRow} disabled={!catalog.length} aria-label="Aggiungi servizio" title="Aggiungi servizio">
                  <svg className="overlay_action_icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                </button>
                <p>Il Soggiorno è automatico. Premi + per aggiungere altri servizi.</p>
              </div>

              <div className="overlay_list_rows booking_services_list_rows">
                <div className={`overlay_list_row booking_services_list_row booking_services_stay_row${stayExpanded ? " is_expanded" : ""}`}>
                  <label className="overlay_field booking_services_slot_1"><span>Tipo Servizio</span><input value="Soggiorno" disabled readOnly /></label>
                  <label className="overlay_field booking_services_slot_2"><span>Data Addebito</span><VestaDateInput value={stay.chargeDate || String(booking.check_in || "").slice(0, 10)} outputFormat="iso" onChange={(value) => patchStay({ chargeDate: value })} /></label>
                  <label className="overlay_field booking_services_slot_3"><span>Tipo Addebito</span><VestaSelect value="DAILY" disabled><option value="DAILY">Giornaliero</option></VestaSelect></label>
                  <label className="overlay_field booking_services_slot_4"><span>Prezzo</span><input type="number" min="0" step="0.01" value={stay.unitPrice ?? 0} onChange={(event) => patchStay({ unitPrice: event.target.value })} /></label>
                  <label className="overlay_field booking_services_slot_5"><span>Quantità (Giorni)</span><input value={stay.quantity ?? stayNights(booking.check_in, booking.check_out)} disabled readOnly /></label>
                  <label className="overlay_field booking_services_slot_6 booking_services_percent_field"><span>% Sconto</span><input type="number" min="0" max="100" step="0.01" value={stay.discountRate ?? 0} onChange={(event) => patchStay({ discountRate: event.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })} /></label>
                  <label className="overlay_field booking_services_slot_7 booking_services_percent_field"><span>% Aliquota IVA</span><input type="number" min="0" max="100" step="0.01" value={stay.vatRate ?? 0} onChange={(event) => patchStay({ vatRate: event.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })} /></label>
                  <label className="overlay_field booking_services_slot_8"><span>Inserito da</span><ServiceSourceSelect value="AUTOMATIC" /></label>
                  <button type="button" className="overlay_list_group_toggle vesta_filters_toggle booking_services_group_toggle" onClick={() => setExpandedRows((current) => ({ ...current, stay: !current.stay }))} aria-expanded={stayExpanded} aria-label={stayExpanded ? "Nascondi valori aggiuntivi soggiorno" : "Espandi valori soggiorno"} title={stayExpanded ? "Nascondi valori aggiuntivi" : "Espandi gruppo valori"}><span className="overlay_list_group_toggle_icon vesta_filters_toggle_icon" aria-hidden="true"><span /><span /></span></button>
                  <label className="overlay_field booking_services_slot_9 booking_services_extra_field"><span>Imponibile</span><input value={formatMoney(stayComputed.taxable)} disabled readOnly /></label>
                  <label className="overlay_field booking_services_slot_10 booking_services_extra_field"><span>Netto</span><input value={formatMoney(stayComputed.net)} disabled readOnly /></label>
                  <label className="overlay_field booking_services_slot_11 booking_services_extra_field"><span>Imposta</span><input value={formatMoney(stayComputed.tax)} disabled readOnly /></label>
                  <label className="overlay_field booking_services_slot_12 booking_services_extra_field"><span>Ivato</span><input value={formatMoney(stayComputed.gross)} disabled readOnly /></label>
                  <label className="overlay_field booking_services_notes_field booking_services_extra_field"><span>Note</span><DefaultNoteTextarea value={stay.note || ""} noteType="Servizio" rows={2} onChange={(value) => patchStay({ note: value })} /></label>
                </div>

                {rows.map((row, index) => {
                  const computed = rowTotals(row);
                  const rowKey = row._id || row._localId || `service-${index}`;
                  const expanded = Boolean(expandedRows[rowKey]);
                  const quantityLabel = row.chargeType === "HOURLY" ? "Quantità (Ore)" : row.chargeType === "DAILY" ? "Quantità (Giorni)" : "Quantità";
                  const isAccumulation = row.chargeType === "ACCUMULO";
                  return (
                    <div className={`overlay_list_row booking_services_list_row${expanded ? " is_expanded" : ""}`} key={rowKey}>
                      <label className="overlay_field booking_services_slot_1 booking_services_field_service"><span>Tipo Servizio <span className="overlay_required_mark" aria-hidden="true">*</span></span><VestaSelect value={row.service_id || ""} onChange={(event) => chooseService(index, event.target.value)}><option value="">Seleziona servizio</option>{catalog.map((item) => <option key={item._id} value={item._id}>{item.description}</option>)}</VestaSelect></label>
                      <label className="overlay_field booking_services_slot_2"><span>Data Addebito <span className="overlay_required_mark" aria-hidden="true">*</span></span><VestaDateInput value={row.chargeDate || ""} outputFormat="iso" onChange={(value) => patchRow(index, { chargeDate: value })} /></label>
                      <label className="overlay_field booking_services_slot_3 booking_services_charge_type_field"><span>Tipo Addebito</span><div className="booking_services_charge_control"><VestaSelect value={row.chargeType || "FORFAIT"} onChange={(event) => patchRow(index, { chargeType: event.target.value })}><option value="FORFAIT">Forfait</option><option value="DAILY">Giornaliero</option><option value="HOURLY">Orario</option><option value="ACCUMULO">Accumulo</option></VestaSelect>{isAccumulation && <button type="button" className="booking_services_accumulation_button" onClick={() => setAccumulationIndex(index)}>Dettaglio</button>}</div></label>
                      <label className="overlay_field booking_services_slot_4"><span>Prezzo</span><input type="number" min="0" step="0.01" value={row.unitPrice} disabled={isAccumulation} onChange={(event) => patchRow(index, { unitPrice: event.target.value })} /></label>
                      <button type="button" className="overlay_contact_remove_button overlay_list_remove_button booking_services_remove_button" onClick={() => removeServiceRow(index)} aria-label="Elimina servizio" title="Elimina servizio" tabIndex={-1}>×</button>
                      <label className="overlay_field booking_services_slot_5"><span>{quantityLabel}</span><input type="number" min="0.01" step="0.01" value={row.quantity} disabled={isAccumulation} onChange={(event) => patchRow(index, { quantity: event.target.value })} /></label>
                      <label className="overlay_field booking_services_slot_6 booking_services_percent_field"><span>% Sconto</span><input type="number" min="0" max="100" step="0.01" value={row.discountRate ?? 0} onChange={(event) => patchRow(index, { discountRate: event.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })} /></label>
                      <label className="overlay_field booking_services_slot_7 booking_services_percent_field"><span>% Aliquota IVA</span><input type="number" min="0" max="100" step="0.01" value={row.vatRate ?? 0} onChange={(event) => patchRow(index, { vatRate: event.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })} /></label>
                      <label className="overlay_field booking_services_slot_8"><span>Inserito da</span><ServiceSourceSelect value={row.entrySource || "MANUAL_DESKTOP"} /></label>
                      <button type="button" className="overlay_list_group_toggle vesta_filters_toggle booking_services_group_toggle" onClick={() => setExpandedRows((current) => ({ ...current, [rowKey]: !current[rowKey] }))} aria-expanded={expanded} aria-label={expanded ? "Nascondi valori aggiuntivi servizio" : "Espandi valori servizio"} title={expanded ? "Nascondi valori aggiuntivi" : "Espandi gruppo valori"}><span className="overlay_list_group_toggle_icon vesta_filters_toggle_icon" aria-hidden="true"><span /><span /></span></button>
                      <label className="overlay_field booking_services_slot_9 booking_services_extra_field"><span>Imponibile</span><input value={formatMoney(computed.taxable)} disabled readOnly /></label>
                      <label className="overlay_field booking_services_slot_10 booking_services_extra_field"><span>Netto</span><input value={formatMoney(computed.net)} disabled readOnly /></label>
                      <label className="overlay_field booking_services_slot_11 booking_services_extra_field"><span>Imposta</span><input value={formatMoney(computed.tax)} disabled readOnly /></label>
                      <label className="overlay_field booking_services_slot_12 booking_services_extra_field"><span>Ivato</span><input value={formatMoney(computed.gross)} disabled readOnly /></label>
                      <label className="overlay_field booking_services_notes_field booking_services_extra_field"><span>Note</span><DefaultNoteTextarea value={row.note || ""} noteType="Servizio" rows={2} onChange={(value) => patchRow(index, { note: value })} /></label>
                    </div>
                  );
                })}
                {!rows.length && <div className="overlay_list_empty">Nessun servizio aggiuntivo. Il Soggiorno è già presente.</div>}
              </div>
            </div>
          </div>
          </fieldset>
        </div>

        <div className="booking_services_sticky_totals">
          <div><span>Totale Imponibile</span><strong>{formatMoney(totals.taxable)}</strong></div>
          <div><span>Totale Acconti</span><strong>{formatMoney(advanceTotal)}</strong></div>
          <div><span>Totale Imposta</span><strong>{formatMoney(totals.tax)}</strong></div>
          <div><span>Totale Ivato</span><strong>{formatMoney(totals.gross)}</strong></div>
        </div>

        <div className="overlay_footer">
          <div className="overlay_error_message">{error ? "Impossibile Completare l’Azione Richiesta" : ""}</div>
          <button type="button" className="overlay_save_button overlay_save_close_button" disabled={locked} onClick={onSave}>Salva e Chiudi</button>
        </div>
      </div>
      {accumulationIndex !== null && !locked && <AccumulationDetailOverlay catalog={catalog} row={rows[accumulationIndex]} onSave={saveAccumulation} onClose={() => setAccumulationIndex(null)} />}
    </div>
  );
}
export function BookingCheckoutOverlay({ booking, draft, error, saving, paymentMethodCatalog = [], fiscalDocumentTypes = [], onChange, onClose, onSave, onSaveAndClose, onBookingStateChange, activeStayTab = "checkout", onStayTabChange, stayTabErrors = {}, onStayTabErrorChange = null }) {
  const rows = checkoutRows(booking);
  const activeFiscalTypes = (fiscalDocumentTypes || []).filter((item) => item?.active !== false);
  const checkoutDocumentTypes = activeFiscalTypes.filter((item) => (item.allowedPurposes || []).includes("CHECKOUT") && ["INVOICE", "RECEIPT"].includes(String(item.code || "").toUpperCase()));
  const selectedDocumentType = activeFiscalTypes.find((item) => String(item.code || "").toUpperCase() === String(draft.documentType || "").toUpperCase());
  const configuredReversalType = String(selectedDocumentType?.reversalDocumentType || "").toUpperCase();
  const reversalDocumentTypes = activeFiscalTypes.filter((item) => (item.allowedPurposes || []).includes("REVERSAL") && (!configuredReversalType || String(item.code || "").toUpperCase() === configuredReversalType));
  const selectedReversalType = activeFiscalTypes.find((item) => String(item.code || "").toUpperCase() === String(draft.reversalDocumentType || "").toUpperCase());
  const documentLabel = selectedDocumentType?.description || DOCUMENT_TYPE_LABELS[draft.documentType] || "Fattura";
  const isInvoice = String(selectedDocumentType?.counterDocumentType || (String(draft.documentType || "").toUpperCase() === "RECEIPT" ? "RECEIPT" : "INVOICE")).toUpperCase() === "INVOICE";
  const fiscalSection = selectedDocumentType?.fiscalSection || (isInvoice ? "FT" : "RC");
  const checkoutPayments = normalizeBookingPayments(draft.payments, 0, "", true);
  const advanceDepositTotal = checkoutPayments
    .filter((payment) => ["ADVANCE", "DEPOSIT"].includes(String(payment?.paymentType || "").toUpperCase()))
    .reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);
  const baseTaxableTotal = rows.reduce((sum, row) => sum + Number(row.netTotal ?? row.taxableTotal ?? 0), 0);
  const unconditionalDiscountAmount = Math.min(baseTaxableTotal, Math.max(0, numberValue(draft.unconditionalDiscountAmount, 0)));
  const unconditionalDiscountRate = baseTaxableTotal > 0 ? unconditionalDiscountAmount / baseTaxableTotal * 100 : 0;
  const unconditionalDiscountFactor = baseTaxableTotal > 0 ? (baseTaxableTotal - unconditionalDiscountAmount) / baseTaxableTotal : 1;
  const adjustedVatRows = rows.map((row) => {
    const taxable = Math.max(0, Number(row.netTotal ?? row.taxableTotal ?? 0) * unconditionalDiscountFactor);
    const vatRate = explicitVatRate(row);
    const tax = vatRate === null ? 0 : taxable * vatRate / 100;
    return { ...row, taxableTotal: taxable, netTotal: taxable, taxAmount: tax, grossTotal: taxable + tax };
  });
  const taxableTotal = adjustedVatRows.reduce((sum, row) => sum + Number(row.taxableTotal || 0), 0);
  const taxTotal = adjustedVatRows.reduce((sum, row) => sum + Number(row.taxAmount || 0), 0);
  const subtotalBeforeRounding = taxableTotal + taxTotal;
  const storedRoundingAmount = numberValue(draft.roundingAmount, 0);
  const targetGrossTotal = draft.targetGrossTotal === "" || draft.targetGrossTotal === null || draft.targetGrossTotal === undefined
    ? Math.max(0, subtotalBeforeRounding + storedRoundingAmount)
    : Math.max(0, numberValue(draft.targetGrossTotal, subtotalBeforeRounding));
  const roundingAmount = targetGrossTotal - subtotalBeforeRounding;
  const grossTotal = targetGrossTotal;
  const roundingLabel = roundingAmount > 0.000001 ? "Abbuono Attivo" : roundingAmount < -0.000001 ? "Abbuono Passivo" : "Abbuono";
  const orientation = draft.orientation === "LANDSCAPE" ? "landscape" : "portrait";
  const reversalOrientation = draft.reversalOrientation === "LANDSCAPE" ? "landscape" : "portrait";
  const stayClosed = String(booking?.status || "").toUpperCase() === "CHECKED_OUT";
  const checkoutIssued = stayClosed && Boolean(draft.documentId);
  const reversalIssued = checkoutIssued && Boolean(draft.reversalDocumentId);
  const reversalDocumentLabel = selectedReversalType?.description || DOCUMENT_TYPE_LABELS[draft.reversalDocumentType] || "Nota di Credito";
  const reversalFiscalSection = selectedReversalType?.fiscalSection || (draft.reversalDocumentType === "RECEIPT_REVERSAL" ? "SR" : "NC");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocumentKind, setPreviewDocumentKind] = useState("checkout");
  const [pendingPrintKind, setPendingPrintKind] = useState("");
  const previewIsInvoice = previewDocumentKind === "reversal"
    ? String(selectedReversalType?.counterDocumentType || (draft.reversalDocumentType === "RECEIPT_REVERSAL" ? "RECEIPT" : "INVOICE")).toUpperCase() === "INVOICE"
    : isInvoice;
  const [reversalSaving, setReversalSaving] = useState(false);
  const [reversalError, setReversalError] = useState("");
  const [documentActionError, setDocumentActionError] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState("");
  const [previewPage, setPreviewPage] = useState(0);
  const [numberPreviewLoading, setNumberPreviewLoading] = useState(false);
  const [numberPreviewError, setNumberPreviewError] = useState("");
  const [reversalNumberPreviewLoading, setReversalNumberPreviewLoading] = useState(false);
  const [reversalNumberPreviewError, setReversalNumberPreviewError] = useState("");
  const [paymentEditorHasError, setPaymentEditorHasError] = useState(false);
  const [numberingRefreshTick, setNumberingRefreshTick] = useState(0);
  const printPagesRef = useRef(null);
  const manualNumberEditedRef = useRef(Boolean(draft.numberingMode === "MANUAL" && /^\d+$/.test(String(draft.documentNumber || ""))));
  const reversalManualNumberEditedRef = useRef(Boolean(draft.reversalDocumentId && draft.reversalNumberingMode === "MANUAL"));
  const companyProfile = booking.company_profile || {};
  const companyName = companyProfile.description || booking.company_name || getCompanyDisplayName();
  const companyLogoUrl = String(booking.company_logo_url || "").trim();
  const legalSite = booking.legal_site || {};
  const customerName = booking.invoice_name || booking.guest_name || "-";
  const guestCustomerName = booking.guest_customer_name || booking.guest_name || "-";
  const customerAddress = booking.invoice_address || booking.customer_address || "-";
  const customerPhone = booking.invoice_phone || booking.customer_phone || "-";
  const customerEmail = booking.invoice_email || booking.customer_email || "-";
  const customerFiscalCode = booking.invoice_fiscal_code || booking.customer_fiscal_code || "-";
  const customerVatNumber = booking.invoice_vat_number || booking.customer_vat_number || "-";
  const customerSdiCode = booking.invoice_sdi_code || "-";
  const nights = stayNights(booking.check_in, booking.check_out);
  const paymentSummary = bookingPaymentSummary({ ...booking, payments: checkoutPayments });
  const displayRows = rows;
  const vatSummary = vatBreakdown(adjustedVatRows);
  const issueDate = draft.emissionDate || "";
  const reversalIssueDate = draft.reversalEmissionDate || "";
  const previewOrientation = previewDocumentKind === "reversal" ? reversalOrientation : orientation;

  useEffect(() => {
    const refresh = () => setNumberingRefreshTick((current) => current + 1);
    window.addEventListener("vesta:fiscal-numbering-refresh", refresh);
    return () => window.removeEventListener("vesta:fiscal-numbering-refresh", refresh);
  }, []);

  useEffect(() => {
    if (draft.emissionDate) return;
    onChange({ emissionDate: todayIso() });
  }, [draft.emissionDate, onChange]);

  const legalAddress = useMemo(() => {
    const street = [legalSite.address, legalSite.streetNumber].filter(Boolean).join(" ").trim();
    return street || "-";
  }, [legalSite.address, legalSite.streetNumber]);

  const legalLocation = useMemo(() => {
    const city = [legalSite.cap, legalSite.city].filter(Boolean).join(" ").trim();
    const province = String(legalSite.province || "").trim();
    return [city, province ? `(${province})` : ""].filter(Boolean).join(" ") || "-";
  }, [legalSite.cap, legalSite.city, legalSite.province]);

  const detailDocumentRows = useMemo(() => displayRows.map((row) => [
    row.description,
    formatMoney(row.unitPrice),
    String(row.quantity),
    formatMoney(row.taxableTotal),
    Number(row.discountRate || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 }),
    formatMoney(row.netTotal ?? row.taxableTotal),
    explicitVatRate(row) === null ? "-" : Number(row.vatRate || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 }),
    formatMoney(row.taxAmount),
    formatMoney(row.grossTotal),
  ]), [displayRows]);

  const previewPages = useMemo(() => {
    const pageSize = previewOrientation === "landscape" ? 5 : 8;
    const pages = [];
    for (let index = 0; index < detailDocumentRows.length; index += pageSize) {
      pages.push(detailDocumentRows.slice(index, index + pageSize));
    }
    return pages.length ? pages : [[]];
  }, [detailDocumentRows, previewOrientation]);

  const previewItem = useMemo(() => ({
    title: `${previewDocumentKind === "reversal" ? reversalDocumentLabel : `${documentLabel} Check Out`} - Camera ${booking.room_number || "-"}`,
    fields: ["Descrizione", "Prezzo", "Qta", "Imponibile", "% Sconto", "Netto", "% IVA", "Imposta", "Ivato"],
  }), [previewDocumentKind, reversalDocumentLabel, documentLabel, booking.room_number]);

  const safePreviewPage = Math.min(Math.max(previewPage, 0), Math.max(previewPages.length - 1, 0));
  const checkoutNumberError = numberPreviewError || (/numero documento/i.test(String(error || "")) && !/storno/i.test(String(error || "")) ? error : "");
  const checkoutDateError = /data (?:emissione|documento)/i.test(String(error || "")) && !/storno/i.test(String(error || "")) ? error : "";
  const checkoutPaymentsError = /pagament|accont/i.test(String(error || "")) ? error : "";
  const checkoutGeneralError = error && !checkoutNumberError && !checkoutDateError && !checkoutPaymentsError ? error : documentActionError;
  const reversalNumberError = reversalNumberPreviewError || (/numero documento/i.test(String(reversalError || "")) ? reversalError : "");
  const reversalDateError = /data (?:emissione|documento)/i.test(String(reversalError || "")) ? reversalError : "";
  const reversalGeneralError = reversalError && !reversalNumberError && !reversalDateError ? reversalError : "";
  const hasCheckoutErrors = Boolean(error || documentActionError || reversalError || numberPreviewError || reversalNumberPreviewError || paymentEditorHasError);
  useEffect(() => {
    if (!onStayTabChange || !hasCheckoutErrors) return;
    onStayTabErrorChange?.("checkout", true);
  }, [hasCheckoutErrors, onStayTabChange, onStayTabErrorChange]);
  const documentReady = /^\d+$/.test(String(draft.documentNumber || ""))
    && Boolean(draft.emissionDate)
    && !numberPreviewLoading
    && !numberPreviewError;
  const reversalDocumentReady = /^\d+$/.test(String(draft.reversalDocumentNumber || ""))
    && Boolean(draft.reversalEmissionDate)
    && !reversalNumberPreviewLoading
    && !reversalNumberPreviewError;

  useEffect(() => {
    if (!draft.emissionDate) {
      setNumberPreviewLoading(false);
      setNumberPreviewError("");
      return undefined;
    }
    if (draft.numberingMode === "MANUAL" && manualNumberEditedRef.current) return undefined;

    let active = true;
    setNumberPreviewLoading(true);
    setNumberPreviewError("");
    getBookingDocumentNumberPreview({
      documentType: draft.documentType,
      numberingMode: draft.numberingMode,
      emissionDate: draft.emissionDate,
      documentId: draft.documentId || undefined,
    }).then((data) => {
      if (!active) return;
      onChange({ documentNumber: String(data?.nextNumber || "") });
    }).catch((err) => {
      if (!active) return;
      setNumberPreviewError(err?.response?.data?.error || "Impossibile calcolare il Numero Documento");
      onChange({ documentNumber: "" });
    }).finally(() => {
      if (active) setNumberPreviewLoading(false);
    });
    return () => {
      active = false;
    };
  }, [draft.documentId, draft.documentType, draft.emissionDate, draft.numberingMode, onChange, numberingRefreshTick]);

  useEffect(() => {
    if (draft.numberingMode !== "MANUAL" || !manualNumberEditedRef.current || !draft.emissionDate) return undefined;
    const token = String(draft.documentNumber || "").trim();
    if (!/^\d+$/.test(token) || Number(token) <= 0) {
      setNumberPreviewLoading(false);
      setNumberPreviewError("Il Numero Documento manuale deve contenere solo numeri ed essere maggiore di zero");
      return undefined;
    }

    let active = true;
    setNumberPreviewLoading(true);
    setNumberPreviewError("");
    const timer = window.setTimeout(() => {
      getBookingDocumentNumberPreview({
        documentType: draft.documentType,
        numberingMode: "MANUAL",
        documentNumber: token,
        emissionDate: draft.emissionDate,
        documentId: draft.documentId || undefined,
      }).catch((err) => {
        if (!active) return;
        setNumberPreviewError(err?.response?.data?.error || "Numero Documento o Data Documento non coerenti");
      }).finally(() => {
        if (active) setNumberPreviewLoading(false);
      });
    }, 260);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [draft.documentId, draft.documentNumber, draft.documentType, draft.emissionDate, draft.numberingMode]);

  useEffect(() => {
    if (!checkoutIssued || reversalIssued || !draft.reversalEmissionDate) {
      setReversalNumberPreviewLoading(false);
      setReversalNumberPreviewError("");
      return undefined;
    }
    if (draft.reversalNumberingMode === "MANUAL" && reversalManualNumberEditedRef.current) return undefined;

    let active = true;
    setReversalNumberPreviewLoading(true);
    setReversalNumberPreviewError("");
    getBookingDocumentNumberPreview({
      documentType: draft.reversalDocumentType,
      numberingMode: draft.reversalNumberingMode,
      emissionDate: draft.reversalEmissionDate,
      documentId: draft.reversalDocumentId || undefined,
    }).then((data) => {
      if (!active) return;
      onChange({ reversalDocumentNumber: String(data?.nextNumber || "") });
    }).catch((err) => {
      if (!active) return;
      setReversalNumberPreviewError(err?.response?.data?.error || "Impossibile calcolare il Numero Documento storno");
      onChange({ reversalDocumentNumber: "" });
    }).finally(() => {
      if (active) setReversalNumberPreviewLoading(false);
    });
    return () => { active = false; };
  }, [checkoutIssued, reversalIssued, draft.reversalDocumentId, draft.reversalDocumentType, draft.reversalEmissionDate, draft.reversalNumberingMode, onChange, numberingRefreshTick]);

  useEffect(() => {
    if (!checkoutIssued || reversalIssued || draft.reversalNumberingMode !== "MANUAL" || !reversalManualNumberEditedRef.current || !draft.reversalEmissionDate) return undefined;
    const token = String(draft.reversalDocumentNumber || "").trim();
    if (!/^\d+$/.test(token) || Number(token) <= 0) {
      setReversalNumberPreviewLoading(false);
      setReversalNumberPreviewError("Il Numero Documento storno manuale deve contenere solo numeri ed essere maggiore di zero");
      return undefined;
    }
    let active = true;
    setReversalNumberPreviewLoading(true);
    setReversalNumberPreviewError("");
    const timer = window.setTimeout(() => {
      getBookingDocumentNumberPreview({
        documentType: draft.reversalDocumentType,
        numberingMode: "MANUAL",
        documentNumber: token,
        emissionDate: draft.reversalEmissionDate,
        documentId: draft.reversalDocumentId || undefined,
      }).catch((err) => {
        if (!active) return;
        setReversalNumberPreviewError(err?.response?.data?.error || "Numero Documento storno o Data Documento non coerenti");
      }).finally(() => {
        if (active) setReversalNumberPreviewLoading(false);
      });
    }, 260);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [checkoutIssued, reversalIssued, draft.reversalDocumentId, draft.reversalDocumentNumber, draft.reversalDocumentType, draft.reversalEmissionDate, draft.reversalNumberingMode]);

  function subtotalForDiscountFactor(factor) {
    return rows.reduce((sum, row) => {
      const taxable = Math.max(0, Number(row.netTotal ?? row.taxableTotal ?? 0) * factor);
      const vatRate = explicitVatRate(row);
      return sum + taxable + (vatRate === null ? 0 : taxable * vatRate / 100);
    }, 0);
  }

  function updateUnconditionalDiscountAmount(value) {
    const amount = Math.min(baseTaxableTotal, Math.max(0, numberValue(value, 0)));
    const rate = baseTaxableTotal > 0 ? amount / baseTaxableTotal * 100 : 0;
    const factor = baseTaxableTotal > 0 ? (baseTaxableTotal - amount) / baseTaxableTotal : 1;
    const nextSubtotal = subtotalForDiscountFactor(factor);
    onChange({
      unconditionalDiscountAmount: value === "" ? "" : amount,
      unconditionalDiscountRate: value === "" ? "" : rate,
      targetGrossTotal: Math.round(nextSubtotal * 100) / 100,
      roundingAmount: 0,
    });
  }

  function updateUnconditionalDiscountRate(value) {
    const rate = Math.min(100, Math.max(0, numberValue(value, 0)));
    const amount = baseTaxableTotal * rate / 100;
    const factor = baseTaxableTotal > 0 ? (baseTaxableTotal - amount) / baseTaxableTotal : 1;
    const nextSubtotal = subtotalForDiscountFactor(factor);
    onChange({
      unconditionalDiscountRate: value === "" ? "" : rate,
      unconditionalDiscountAmount: value === "" ? "" : amount,
      targetGrossTotal: Math.round(nextSubtotal * 100) / 100,
      roundingAmount: 0,
    });
  }

  function updateTargetGrossTotal(value) {
    if (value === "") {
      onChange({ targetGrossTotal: "", roundingAmount: 0 });
      return;
    }
    const nextTotal = Math.max(0, numberValue(value, subtotalBeforeRounding));
    onChange({
      targetGrossTotal: value,
      roundingAmount: nextTotal - subtotalBeforeRounding,
    });
  }

  function toggleOrientation() {
    onChange({ orientation: orientation === "landscape" ? "PORTRAIT" : "LANDSCAPE" });
    setPreviewPage(0);
  }

  function toggleReversalOrientation() {
    onChange({ reversalOrientation: reversalOrientation === "landscape" ? "PORTRAIT" : "LANDSCAPE" });
    setPreviewPage(0);
  }

  function handleCompanyLogoError(event) {
    event.currentTarget.closest(".booking_fiscal_company_logo_box")?.classList.add("is_vesta_fallback");
    if (event.currentTarget.src !== vestaLogo) event.currentTarget.src = vestaLogo;
  }

  function printPreview() {
    printFiscalPreviewRoot({ root: printPagesRef.current, orientation: previewOrientation, title: previewItem.title });
  }

  function schedulePrint(kind) {
    setPreviewDocumentKind(kind);
    setPendingPrintKind(kind);
  }

  useEffect(() => {
    if (!pendingPrintKind || pendingPrintKind !== previewDocumentKind) return undefined;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        printPreview();
        setPendingPrintKind("");
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [pendingPrintKind, previewDocumentKind, previewOrientation, previewItem.title, previewPages]);

  async function exportPreview(format) {
    const filenameBase = slugify(previewItem.title);
    setExportError("");
    try {
      const blob = await createFiscalVisualExportBlob({
        root: printPagesRef.current,
        format,
        orientation: previewOrientation,
      });
      downloadBlob(blob, `${filenameBase}.${format === "word" ? "docx" : "pdf"}`);
      setExportOpen(false);
    } catch (_error) {
      setExportError("Impossibile esportare il documento");
    }
  }

  async function cancelCheckoutAndReopen() {
    if (!draft.documentId || saving) return;
    if (reversalIssued) {
      setDocumentActionError(
        String(draft.reversalDocumentType || "").toUpperCase() === "RECEIPT_REVERSAL"
          ? "Impossibile riaprire la ricevuta: è presente uno Storno Ricevuta collegato"
          : "Impossibile riaprire la fattura: è presente una Nota di Credito collegata"
      );
      return;
    }
    setDocumentActionError("");
    try {
      const result = await cancelBookingCheckoutDocument(booking._id, draft.documentId);
      const reopenedPayments = Array.isArray(result?.payments) ? normalizeBookingPayments(result.payments) : checkoutPayments.filter((payment) => payment.paymentType !== "BALANCE");
      const cancelledDocument = result?.item || {};
      const historicalNumber = String(cancelledDocument.documentSequence || cancelledDocument.documentNumber || draft.documentNumber || "").replace(/\D/g, "");
      manualNumberEditedRef.current = /^\d+$/.test(historicalNumber);
      setNumberPreviewError("");
      onChange({
        documentId: "",
        documentType: cancelledDocument.documentType || draft.documentType,
        numberingMode: "MANUAL",
        documentNumber: historicalNumber,
        emissionDate: cancelledDocument.issuedAt ? String(cancelledDocument.issuedAt).slice(0, 10) : draft.emissionDate,
        orientation: "PORTRAIT",
        note: cancelledDocument.note ?? draft.note,
        targetGrossTotal: "",
        roundingAmount: 0,
        payments: reopenedPayments,
      });
      onBookingStateChange?.({
        status: "CHECKED_IN",
        latest_fiscal_document: null,
        latest_checkout_history_document: cancelledDocument,
        payments: reopenedPayments,
      });
      window.setTimeout(notifyFiscalNumberingRefresh, 0);
    } catch (err) {
      setDocumentActionError(err?.response?.data?.error || "Errore annullamento documento fiscale");
    }
  }

  async function toggleReversalDocument() {
    if (!draft.documentId || reversalSaving) return;
    setReversalError("");
    setReversalSaving(true);
    try {
      if (reversalIssued) {
        const result = await cancelBookingReversalDocument(booking._id, draft.reversalDocumentId);
        const cancelled = result?.item || {};
        const historicalNumber = String(cancelled.documentSequence || cancelled.documentNumber || draft.reversalDocumentNumber || "").replace(/\D/g, "");
        reversalManualNumberEditedRef.current = /^\d+$/.test(historicalNumber);
        onChange({
          reversalDocumentId: "",
          reversalDocumentType: cancelled.documentType || draft.reversalDocumentType,
          reversalNumberingMode: "MANUAL",
          reversalDocumentNumber: historicalNumber,
          reversalEmissionDate: cancelled.issuedAt ? String(cancelled.issuedAt).slice(0, 10) : draft.reversalEmissionDate,
          reversalOrientation: "PORTRAIT",
          reversalNote: cancelled.note ?? draft.reversalNote,
        });
        onBookingStateChange?.({ latest_reversal_document: null, latest_reversal_history_document: cancelled });
      } else {
        const result = await issueBookingReversalDocument(booking._id, {
          sourceDocumentId: draft.documentId,
          documentType: draft.reversalDocumentType,
          numberingMode: draft.reversalNumberingMode,
          documentNumber: draft.reversalDocumentNumber,
          emissionDate: draft.reversalEmissionDate,
          orientation: "PORTRAIT",
          note: draft.reversalNote,
        });
        const issued = result?.item || {};
        onChange({
          reversalDocumentId: issued._id || "",
          reversalDocumentType: issued.documentType || draft.reversalDocumentType,
          reversalDocumentNumber: String(issued.documentSequence || issued.documentNumber || draft.reversalDocumentNumber || "").replace(/\D/g, ""),
          reversalEmissionDate: issued.issuedAt ? String(issued.issuedAt).slice(0, 10) : draft.reversalEmissionDate,
          reversalOrientation: draft.reversalOrientation,
          reversalNote: issued.note ?? draft.reversalNote,
        });
        onBookingStateChange?.({ latest_reversal_document: issued });
        schedulePrint("reversal");
      }
      window.setTimeout(notifyFiscalNumberingRefresh, 0);
    } catch (err) {
      const item = err?.response?.data?.item;
      if (item) {
        onChange({
          reversalDocumentId: item._id || "",
          reversalDocumentType: item.documentType || draft.reversalDocumentType,
          reversalDocumentNumber: String(item.documentSequence || item.documentNumber || "").replace(/\D/g, ""),
          reversalEmissionDate: item.issuedAt ? String(item.issuedAt).slice(0, 10) : draft.reversalEmissionDate,
          reversalOrientation: item.orientation || draft.reversalOrientation,
          reversalNote: item.note || draft.reversalNote,
        });
      }
      setReversalError(err?.response?.data?.error || "Errore gestione storno soggiorno");
    } finally {
      setReversalSaving(false);
    }
  }

  async function concludeStayAndPrint() {
    if (saving || checkoutIssued || !onSave) return;
    const saved = await onSave();
    if (saved) {
      schedulePrint("checkout");
      window.setTimeout(notifyFiscalNumberingRefresh, 0);
    }
  }

  function openCheckoutPreview() {
    setPreviewDocumentKind("checkout");
    setPreviewPage(0);
    setPreviewOpen(true);
  }

  function openReversalPreview() {
    setPreviewDocumentKind("reversal");
    setPreviewPage(0);
    setPreviewOpen(true);
  }

  function printReversal() {
    schedulePrint("reversal");
  }

  function renderHeader() {
    return (
      <>
        <div className="booking_fiscal_report_header booking_fiscal_report_header_v2">
          <div className={`booking_fiscal_company_logo_box${companyLogoUrl ? "" : " is_vesta_fallback"}`}>
            <img src={companyLogoUrl || vestaLogo} onError={handleCompanyLogoError} alt={companyName || "VestaMNG"} />
          </div>
          <div className="booking_fiscal_company_identity">
            <strong>{companyName || "VestaMNG"}</strong>
            <span>P. IVA: {companyProfile.vatNumber || "-"}</span>
            <span>Codice Fiscale: {companyProfile.fiscalCode || "-"}</span>
            <span>Capitale Sociale: {Number(companyProfile.shareCapital || 0) > 0 ? formatMoney(companyProfile.shareCapital) : "-"}</span>
            <span>REA: {companyProfile.reaCode || "-"}</span>
            <span>Codice SDI: {companyProfile.sdiCode || "-"}</span>
          </div>
          <div className="booking_fiscal_legal_site">
            <span><b>Indirizzo:</b> {legalAddress}</span>
            <span><b>Località:</b> {legalLocation}</span>
            <span><b>Telefono:</b> {legalSite.phone || "-"}</span>
            <span><b>Email:</b> {legalSite.email || "-"}</span>
            <span><b>Banca:</b> {[companyProfile.primaryBank?.bankName, companyProfile.primaryBank?.branchName].filter(Boolean).join(" - ") || "-"} · <b>IBAN:</b> {companyProfile.primaryBank?.iban || companyProfile.iban || "-"}</span>
          </div>
          <div className="booking_fiscal_header_spacer" aria-hidden="true" />
          <div className="booking_fiscal_vesta_logo_box">
            <img src={vestaLogo} alt="Vesta" />
          </div>
        </div>
        <div className="booking_fiscal_report_rule" />
      </>
    );
  }

  function renderInfoSections() {
    const customerFields = [
      ["Intestatario Fattura", customerName, "is_wide is_primary_value"],
      ["Indirizzo", customerAddress, "is_wide"],
      ["Codice Fiscale", customerFiscalCode, ""],
      ["Partita IVA", customerVatNumber, ""],
      ["Codice SDI", customerSdiCode, ""],
      ["Telefono", customerPhone, ""],
      ["Mail / PEC", customerEmail, "is_wide"],
    ];
    const bookingFields = [
      ["Ospite", guestCustomerName, "is_wide is_primary_value"],
      ["Camera", booking.room_number || "-", ""],
      ["Tipologia", booking.room_type || "-", ""],
      ["Check In", formatDate(booking.check_in), ""],
      ["Ora Check In", valueToDateTimeInput(booking.check_in)?.slice(11, 16) || "-", ""],
      ["Check Out", formatDate(booking.check_out), ""],
      ["Ora Check Out", valueToDateTimeInput(booking.check_out)?.slice(11, 16) || "-", ""],
      ["Notti", String(nights), ""],
      ["Metodo Pagamento", paymentSummary.methods || "-", ""],
      ["Note Documento", String(previewDocumentKind === "reversal" ? draft.reversalNote : draft.note || "").trim() || "-", "is_wide is_note_value"],
    ];
    return (
      <div className="booking_fiscal_info_sections">
        <section className="booking_fiscal_info_card booking_fiscal_info_card_v2">
          <h4>Dati Cliente</h4>
          <div className="booking_fiscal_field_grid">
            {customerFields.map(([label, value, className]) => (
              <div key={label} className={className || undefined}>
                <span>{label}</span>
                <strong>{value || "-"}</strong>
              </div>
            ))}
          </div>
        </section>
        <section className="booking_fiscal_info_card booking_fiscal_info_card_v2">
          <h4>Dati Soggiorno</h4>
          <div className="booking_fiscal_field_grid">
            {bookingFields.map(([label, value, className]) => (
              <div key={label} className={className || undefined}>
                <span>{label}</span>
                <strong>{value || "-"}</strong>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  function renderDocumentDataSection() {
    return (
      <section className="booking_fiscal_document_data_section">
        <h4>Dati Documento</h4>
        <div className="booking_fiscal_document_data_grid">
          <div><span>Tipo Documento</span><strong>{previewDocumentKind === "reversal" ? reversalDocumentLabel : documentLabel}</strong></div>
          <div><span>Numero / Sezionale</span><strong>{previewDocumentKind === "reversal" ? (draft.reversalDocumentNumber || "-") : (draft.documentNumber || "-")} / {previewDocumentKind === "reversal" ? reversalFiscalSection : fiscalSection}</strong></div>
          <div><span>Data</span><strong>{formatDate(previewDocumentKind === "reversal" ? reversalIssueDate : issueDate)}</strong></div>
        </div>
      </section>
    );
  }

  function renderFiscalRowsTable(pageIndex, pageRows) {
    return (
      <div className="booking_fiscal_table_wrap">
        <table className="booking_fiscal_table booking_fiscal_table_v2">
          <thead>
            <tr>
              <th>Descrizione</th>
              <th>Prezzo</th>
              <th>Qta</th>
              <th>Imponibile</th>
              <th>% Sconto</th>
              <th>Netto</th>
              <th>% IVA</th>
              <th>Imposta</th>
              <th>Ivato</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, rowIndex) => (
              <tr key={`checkout-preview-${pageIndex}-${rowIndex}`}>
                {row.map((cell, cellIndex) => <td key={`${pageIndex}-${rowIndex}-${cellIndex}`}>{cell}</td>)}
              </tr>
            ))}
            {!pageRows.length && <tr><td colSpan="9">Nessun addebito presente</td></tr>}
          </tbody>
        </table>
      </div>
    );
  }

  function renderVatBox() {
    return (
      <div className="booking_invoice_vat_box">
        <div className="booking_invoice_vat_title"><span>Castelletto IVA</span></div>
        <div className="booking_invoice_vat_table">
          <div className="booking_invoice_vat_row booking_invoice_vat_header"><span>% IVA</span><span>Imponibile</span><span>Imposta</span><span>Totale Ivato</span></div>
          {vatSummary.configured.map((entry) => (
            <div className="booking_invoice_vat_row" key={`vat-${entry.rate}`}>
              <strong>{Number(entry.rate).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%</strong>
              <strong>{formatMoney(entry.taxable)}</strong>
              <strong>{formatMoney(entry.vat)}</strong>
              <strong>{formatMoney(entry.total)}</strong>
            </div>
          ))}
          {vatSummary.unconfiguredTotal > 0.00001 && (
            <div className="booking_invoice_vat_row is_unconfigured">
              <strong>-</strong>
              <strong>{formatMoney(vatSummary.unconfiguredTaxable)}</strong>
              <strong>{formatMoney(0)}</strong>
              <strong>{formatMoney(vatSummary.unconfiguredTotal)}</strong>
            </div>
          )}
          {!vatSummary.configured.length && vatSummary.unconfiguredTotal <= 0.00001 && (
            <div className="booking_invoice_vat_empty">Nessun importo IVA</div>
          )}
        </div>
      </div>
    );
  }

  function renderTotals() {
    return (
      <div className="booking_fiscal_totals_panel booking_fiscal_totals_panel_v2">
        <div><span>Totale Imponibile</span><strong>{formatMoney(taxableTotal)}</strong></div>
        <div><span>Totale Acconti / Caparre</span><strong>{formatMoney(advanceDepositTotal)}</strong></div>
        <div><span>Sconto Incondizionato</span><strong>- {formatMoney(unconditionalDiscountAmount)}</strong></div>
        <div><span>Totale Imposta</span><strong>{formatMoney(taxTotal)}</strong></div>
        <div><span>{roundingLabel}</span><strong>{roundingAmount > 0 ? "+ " : roundingAmount < 0 ? "- " : ""}{formatMoney(Math.abs(roundingAmount))}</strong></div>
        <div className="booking_fiscal_document_total"><span>{previewDocumentKind === "reversal" ? `Totale ${reversalDocumentLabel}` : (isInvoice ? "Totale Fattura" : "Totale Ricevuta")}</span><strong>{formatMoney(grossTotal)}</strong></div>
      </div>
    );
  }

  function renderBottom() {
    return (
      <div className={`booking_fiscal_bottom_zone booking_fiscal_bottom_zone_v2 ${previewIsInvoice ? "booking_invoice_bottom_zone" : "booking_receipt_bottom_zone"}`}>
        {previewIsInvoice ? renderVatBox() : <div className="booking_fiscal_bottom_spacer" aria-hidden="true" />}
        {renderTotals()}
      </div>
    );
  }

  function renderPreviewPage(pageIndex, pageRows, printMode = false) {
    const lastPage = pageIndex === previewPages.length - 1;
    return (
      <div className={`utility_page_sheet booking_fiscal_report booking_fiscal_report_v2 ${previewIsInvoice ? "booking_invoice_report" : "booking_receipt_report"}${!printMode ? " utility_screen_sheet" : ""}${previewOrientation === "landscape" ? " is_landscape" : ""}`} key={`checkout-${printMode ? "print" : "screen"}-${pageIndex}`}>
        {renderHeader()}
        {renderInfoSections()}
        {renderDocumentDataSection()}
        {renderFiscalRowsTable(pageIndex, pageRows)}
        {lastPage && renderBottom()}
        <div className="booking_fiscal_report_footer booking_fiscal_report_footer_v2">
          <strong>Pagina {pageIndex + 1} / {previewPages.length}</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay_backdrop booking_checkout_backdrop">
      <div className="overlay_card booking_overlay_card booking_form_overlay_card booking_checkout_overlay_card booking_stay_overlay_card" role="dialog" aria-modal="true" aria-label="Soggiorno">
        <div className="overlay_header">
          <h2>Soggiorno</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        {onStayTabChange && <BookingStayTabs activeTab={activeStayTab} onChange={onStayTabChange} errorTabs={stayTabErrors} />}

        <div className="overlay_body booking_checkout_body">
          <div className="overlay_section">
            <h3>Totali</h3>
            <div className="booking_checkout_rows booking_checkout_rows_v2" role="table" aria-label="Importi documento fiscale">
              <div className="booking_checkout_row booking_checkout_row_header" role="row">
                <div role="columnheader">Descrizione</div>
                <div role="columnheader">Prezzo</div>
                <div role="columnheader">Qta</div>
                <div role="columnheader">Imponibile</div>
                <div role="columnheader">% Sconto</div>
                <div role="columnheader">Netto</div>
                <div role="columnheader">% IVA</div>
                <div role="columnheader">Imposta</div>
                <div role="columnheader">Ivato</div>
              </div>
              {displayRows.map((row) => (
                <div key={row.key} className={`booking_checkout_row${row.key === "issued-advance" ? " is_advance_deduction" : ""}`} role="row">
                  <div role="cell"><strong>{row.description}</strong></div>
                  <div role="cell">{formatMoney(row.unitPrice)}</div>
                  <div role="cell">{row.quantity}</div>
                  <div role="cell">{formatMoney(row.taxableTotal)}</div>
                  <div role="cell">{Number(row.discountRate || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })}</div>
                  <div role="cell">{formatMoney(row.netTotal ?? row.taxableTotal)}</div>
                  <div role="cell">{explicitVatRate(row) === null ? "-" : Number(row.vatRate || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })}</div>
                  <div role="cell">{formatMoney(row.taxAmount)}</div>
                  <div role="cell">{formatMoney(row.grossTotal)}</div>
                </div>
              ))}
            </div>
            <div className="booking_checkout_totals_zone">
              <div className="booking_checkout_adjustments_panel">
                <label className="overlay_field booking_checkout_adjustment_field">
                  <span>Sconto Percentuale</span>
                  <input type="number" min="0" max="100" step="0.01" value={draft.unconditionalDiscountRate === "" ? "" : (draft.unconditionalDiscountRate ?? unconditionalDiscountRate)} disabled={checkoutIssued} onChange={(event) => updateUnconditionalDiscountRate(event.target.value)} />
                </label>
                <label className="overlay_field booking_checkout_adjustment_field">
                  <span>Sconto Importo</span>
                  <input type="number" min="0" step="0.01" value={draft.unconditionalDiscountAmount} disabled={checkoutIssued} onChange={(event) => updateUnconditionalDiscountAmount(event.target.value)} />
                </label>
                <label className="overlay_field booking_checkout_adjustment_field">
                  <span>{isInvoice ? "Totale Fattura" : "Totale Ricevuta"}</span>
                  <input type="number" min="0" step="0.01" value={draft.targetGrossTotal === "" ? Math.round(targetGrossTotal * 100) / 100 : draft.targetGrossTotal} disabled={checkoutIssued} onChange={(event) => updateTargetGrossTotal(event.target.value)} />
                </label>
                <label className="overlay_field booking_checkout_adjustment_field booking_checkout_abbuono_field">
                  <span>{roundingLabel}</span>
                  <input value={`${roundingAmount > 0 ? "+ " : roundingAmount < 0 ? "- " : ""}${formatMoney(Math.abs(roundingAmount))}`} disabled readOnly />
                </label>
              </div>
              <div className="booking_checkout_totals_summary">
                <div><span>Totale Imponibile</span><strong>{formatMoney(taxableTotal)}</strong></div>
                <div><span>Totale Acconti / Caparre</span><strong>{formatMoney(advanceDepositTotal)}</strong></div>
                <div><span>Sconto Incondizionato</span><strong>- {formatMoney(unconditionalDiscountAmount)}</strong></div>
                <div><span>Totale Imposta</span><strong>{formatMoney(taxTotal)}</strong></div>
                <div><span>{roundingLabel}</span><strong>{roundingAmount > 0 ? "+ " : roundingAmount < 0 ? "- " : ""}{formatMoney(Math.abs(roundingAmount))}</strong></div>
                <div><span>{isInvoice ? "Totale Fattura" : "Totale Ricevuta"}</span><strong>{formatMoney(grossTotal)}</strong></div>
              </div>
            </div>
          </div>

          <div className="overlay_section booking_checkout_payments_section">
            <h3>Pagamenti</h3>
            <BookingPaymentsEditor
              payments={checkoutPayments}
              paymentMethodCatalog={paymentMethodCatalog}
              fiscalDocumentTypes={fiscalDocumentTypes}
              bookingId={booking._id}
              onChange={(payments) => onChange({ payments })}
              onErrorStateChange={setPaymentEditorHasError}
              externalError={checkoutPaymentsError}
              locked={checkoutIssued}
            />
          </div>

          <div className="overlay_section booking_checkout_additional_section booking_checkout_conclusion_section">
            <h3>Conclusione Soggiorno e Documenti</h3>
            <div className="booking_checkout_document_line">
              <label className="overlay_field booking_checkout_document_field">
                <OverlayFieldLabel required>Tipo Documento</OverlayFieldLabel>
                <VestaSelect disabled={checkoutIssued} value={draft.documentType} onChange={(event) => { manualNumberEditedRef.current = false; setNumberPreviewError(""); onChange({ documentType: event.target.value, documentNumber: "" }); }}>
                  {(checkoutDocumentTypes.length ? checkoutDocumentTypes : [
                    { code: "INVOICE", description: "Fattura" },
                    { code: "RECEIPT", description: "Ricevuta" },
                  ]).map((item) => <option key={item.code} value={item.code}>{item.description}</option>)}
                </VestaSelect>
              </label>
              <button
                type="button"
                tabIndex={-1}
                disabled={checkoutIssued}
                className={`booking_checkout_number_toggle${draft.numberingMode === "MANUAL" ? " is_manual" : " is_auto"}`}
                onClick={() => { manualNumberEditedRef.current = false; setNumberPreviewError(""); onChange({ numberingMode: draft.numberingMode === "AUTO" ? "MANUAL" : "AUTO" }); }}
                aria-label={`Numerazione ${draft.numberingMode === "AUTO" ? "automatica" : "manuale"}. Clicca per cambiare modalità`}
                aria-pressed={draft.numberingMode === "MANUAL"}
              >
                <span className="booking_checkout_number_toggle_knob" aria-hidden="true" />
                <span className="booking_checkout_number_toggle_text">{draft.numberingMode === "AUTO" ? "Automatico" : "Manuale"}</span>
              </button>
              <label className={`overlay_field booking_checkout_number_field${checkoutNumberError ? " overlay_validation_invalid" : ""}`}>
                <OverlayFieldLabel required>Numero Documento</OverlayFieldLabel>
                <input
                  className="booking_checkout_number_input"
                  value={draft.documentNumber || ""}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  readOnly={checkoutIssued || draft.numberingMode === "AUTO"}
                  disabled={checkoutIssued}
                  onChange={(event) => {
                    if (draft.numberingMode !== "MANUAL" || checkoutIssued) return;
                    const digits = event.target.value.replace(/\D/g, "").slice(0, 9).replace(/^0+(?=\d)/, "");
                    manualNumberEditedRef.current = true;
                    onChange({ documentNumber: digits });
                  }}
                  aria-label="Numero Documento"
                />
                <OverlayFieldError message={checkoutNumberError} />
              </label>
              <label className={`overlay_field booking_checkout_emission_date_field${checkoutDateError ? " overlay_validation_invalid" : ""}`}>
                <OverlayFieldLabel required>Data Documento</OverlayFieldLabel>
                <VestaDateInput value={draft.emissionDate} outputFormat="iso" disabled={checkoutIssued} onChange={(value) => !checkoutIssued && onChange({ emissionDate: value })} />
                <OverlayFieldError message={checkoutDateError} />
              </label>
            </div>
            <div className="overlay_fields_grid booking_checkout_document_note_row">
              <label className="overlay_field overlay_field_full booking_checkout_note">
                <span>Note Documento</span>
                <DefaultNoteTextarea rows="3" value={draft.note} noteType="Documento" disabled={checkoutIssued} onChange={(value) => !checkoutIssued && onChange({ note: value })} />
              </label>
            </div>
            <div className="overlay_field booking_checkout_document_command_row">
              <div className="booking_checkout_icon_actions is_left" aria-label="Azioni preparazione documento conclusione soggiorno">
                <UtilityActionIconButton action="orientation" orientation={orientation} onClick={toggleOrientation} />
                <UtilityActionIconButton action="preview" onClick={openCheckoutPreview} disabled={checkoutIssued} title="Anteprima di stampa" />
              </div>
              <div className={`overlay_field booking_checkout_primary_action_field${checkoutGeneralError ? " is_invalid" : ""}`}>
                <OverlayFieldError message={checkoutGeneralError} />
                <button
                  type="button"
                  className={`overlay_save_button booking_checkout_primary_document_button${checkoutIssued ? " is_cancel" : ""}`}
                  disabled={saving || (!checkoutIssued && !documentReady)}
                  onClick={checkoutIssued ? cancelCheckoutAndReopen : concludeStayAndPrint}
                >
                  {saving ? "Operazione..." : checkoutIssued ? "Riapri Soggiorno - Annulla Documento" : "Concludi Soggiorno - Emetti Documento"}
                </button>
              </div>
              <div className="booking_checkout_icon_actions is_right" aria-label="Azioni documento conclusione soggiorno emesso">
                <UtilityActionIconButton action="print" onClick={() => schedulePrint("checkout")} disabled={!checkoutIssued} title="Ristampa" />
                <UtilityActionIconButton action="export" onClick={() => { setPreviewDocumentKind("checkout"); setExportError(""); setExportOpen(true); }} disabled={!checkoutIssued} />
              </div>
            </div>

          </div>

          {checkoutIssued && (
            <div className="overlay_section booking_checkout_additional_section booking_checkout_reversal_section">
              <h3>Storno Soggiorno</h3>
            <div className="booking_checkout_document_line booking_checkout_reversal_document_line">
              <label className="overlay_field booking_checkout_document_field">
                <OverlayFieldLabel required>Tipo Documento Storno</OverlayFieldLabel>
                <VestaSelect disabled={!checkoutIssued || reversalIssued} value={draft.reversalDocumentType} onChange={(event) => { reversalManualNumberEditedRef.current = false; setReversalNumberPreviewError(""); onChange({ reversalDocumentType: event.target.value, reversalDocumentNumber: "" }); }}>
                  {(reversalDocumentTypes.length ? reversalDocumentTypes : [
                    { code: "CREDIT_NOTE", description: "Nota di Credito" },
                    { code: "RECEIPT_REVERSAL", description: "Storno Ricevuta" },
                  ]).map((item) => <option key={item.code} value={item.code}>{item.description}</option>)}
                </VestaSelect>
              </label>
              <button
                type="button"
                tabIndex={-1}
                disabled={!checkoutIssued || reversalIssued}
                className={`booking_checkout_number_toggle${draft.reversalNumberingMode === "MANUAL" ? " is_manual" : " is_auto"}`}
                onClick={() => { reversalManualNumberEditedRef.current = false; setReversalNumberPreviewError(""); onChange({ reversalNumberingMode: draft.reversalNumberingMode === "AUTO" ? "MANUAL" : "AUTO", reversalDocumentNumber: "" }); }}
                aria-label={`Numerazione storno ${draft.reversalNumberingMode === "AUTO" ? "automatica" : "manuale"}. Clicca per cambiare modalità`}
                aria-pressed={draft.reversalNumberingMode === "MANUAL"}
              >
                <span className="booking_checkout_number_toggle_knob" aria-hidden="true" />
                <span className="booking_checkout_number_toggle_text">{draft.reversalNumberingMode === "AUTO" ? "Automatico" : "Manuale"}</span>
              </button>
              <label className={`overlay_field booking_checkout_number_field${reversalNumberError ? " overlay_validation_invalid" : ""}`}>
                <OverlayFieldLabel required>Numero Documento</OverlayFieldLabel>
                <input
                  className="booking_checkout_number_input"
                  value={draft.reversalDocumentNumber || ""}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  readOnly={reversalIssued || draft.reversalNumberingMode === "AUTO"}
                  disabled={!checkoutIssued || reversalIssued}
                  onChange={(event) => {
                    if (draft.reversalNumberingMode !== "MANUAL" || reversalIssued) return;
                    const digits = event.target.value.replace(/\D/g, "").slice(0, 9).replace(/^0+(?=\d)/, "");
                    reversalManualNumberEditedRef.current = true;
                    onChange({ reversalDocumentNumber: digits });
                  }}
                  aria-label="Numero Documento Storno"
                />
                <OverlayFieldError message={reversalNumberError} />
              </label>
              <label className={`overlay_field booking_checkout_emission_date_field${reversalDateError ? " overlay_validation_invalid" : ""}`}>
                <OverlayFieldLabel required>Data Documento</OverlayFieldLabel>
                <VestaDateInput value={draft.reversalEmissionDate} outputFormat="iso" disabled={!checkoutIssued || reversalIssued} onChange={(value) => checkoutIssued && !reversalIssued && onChange({ reversalEmissionDate: value })} />
                <OverlayFieldError message={reversalDateError} />
              </label>
            </div>
            <div className="overlay_fields_grid booking_checkout_document_note_row">
              <label className="overlay_field overlay_field_full booking_checkout_note">
                <span>Note Documento Storno</span>
                <DefaultNoteTextarea rows="3" value={draft.reversalNote} noteType="Documento" disabled={!checkoutIssued || reversalIssued} onChange={(value) => checkoutIssued && !reversalIssued && onChange({ reversalNote: value })} />
              </label>
            </div>
            <div className="overlay_field booking_checkout_document_command_row is_reversal">
              <div className="booking_checkout_icon_actions is_left" aria-label="Azioni preparazione storno soggiorno">
                <UtilityActionIconButton action="orientation" orientation={reversalOrientation} onClick={toggleReversalOrientation} />
                <UtilityActionIconButton action="preview" onClick={openReversalPreview} disabled={reversalIssued} title="Anteprima storno soggiorno" />
              </div>
              <div className={`overlay_field booking_checkout_primary_action_field${reversalGeneralError ? " is_invalid" : ""}`}>
                <OverlayFieldError message={reversalGeneralError} />
                <button
                  type="button"
                  className={`overlay_save_button booking_checkout_primary_document_button${reversalIssued ? " is_cancel" : ""}`}
                  disabled={!checkoutIssued || reversalSaving || (!reversalIssued && !reversalDocumentReady)}
                  onClick={toggleReversalDocument}
                >
                  {reversalSaving ? "Operazione..." : reversalIssued ? "Riprendi Soggiorno - Annulla Documento" : "Storna Soggiorno - Emetti Documento"}
                </button>
              </div>
              <div className="booking_checkout_icon_actions is_right" aria-label="Azioni storno soggiorno emesso">
                <UtilityActionIconButton action="print" onClick={printReversal} disabled={!reversalIssued} title="Ristampa" />
                <UtilityActionIconButton action="export" onClick={() => { setPreviewDocumentKind("reversal"); setExportError(""); setExportOpen(true); }} disabled={!reversalIssued} />
              </div>
            </div>

            </div>
          )}
        </div>

        <div className="booking_checkout_print_area utility_preview_print_area" aria-hidden="true">
          <div className="utility_print_pages" ref={printPagesRef}>
            {previewPages.map((pageRows, pageIndex) => renderPreviewPage(pageIndex, pageRows, true))}
          </div>
        </div>

        <div className="overlay_footer booking_checkout_footer">
          <div className="overlay_error_message">{hasCheckoutErrors ? "Impossibile Completare l’Azione Richiesta" : ""}</div>
          <button type="button" className="overlay_save_button overlay_save_close_button" disabled={saving} onClick={checkoutIssued ? onClose : (onSaveAndClose || onSave)}>Salva e Chiudi</button>
        </div>
      </div>

      <UtilityFullscreenPreview
        open={previewOpen}
        orientation={previewOrientation}
        currentPage={safePreviewPage}
        pageCount={previewPages.length}
        onPageChange={setPreviewPage}
        onPrint={printPreview}
        onExport={() => { setExportError(""); setExportOpen(true); }}
        onToggleOrientation={previewDocumentKind === "reversal" ? toggleReversalOrientation : toggleOrientation}
        onClose={() => setPreviewOpen(false)}
      >
        {renderPreviewPage(safePreviewPage, previewPages[safePreviewPage] || [])}
      </UtilityFullscreenPreview>

      {createPortal(
        <UtilityExportOverlay open={exportOpen} title={previewItem.title} documentMode formats={["pdf", "word"]} error={exportError} onClose={() => setExportOpen(false)} onExport={exportPreview} />,
        window.document.body
      )}
    </div>
  );
}

function BookingAccountOptionsMenu({ open, menuRef, onToggle, onReset }) {
  return (
    <div className="management_options_menu" ref={menuRef}>
      <button type="button" className="management_options_button" onClick={onToggle} aria-label="Opzioni fatturazione" aria-expanded={open}>
        <span></span><span></span><span></span>
      </button>
      {open && (
        <div className="management_options_dropdown">
          <button type="button" className="management_options_item" onClick={onReset}>Azzera ricerca e filtri</button>
        </div>
      )}
    </div>
  );
}

export default function BookingAccounts() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [fiscalDocumentTypes, setFiscalDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ q: "", from: "", to: "" });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [checkoutBooking, setCheckoutBooking] = useState(null);
  const [activeStayTab, setActiveStayTab] = useState("booking");
  const [stayTabErrors, setStayTabErrors] = useState({ booking: false, services: false, checkout: false });
  const stayTabErrorBookingIdRef = useRef("");
  const [serviceDraft, setServiceDraft] = useState({ stay: null, rows: [], removedIds: [] });
  const [checkoutDraft, setCheckoutDraft] = useState(EMPTY_CHECKOUT_DRAFT);
  const [overlayError, setOverlayError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editError, setEditError] = useState("");
  const [editData, setEditData] = useState({ customers: [], rooms: [], sites: [], roomCatalogs: { roomTypes: [], bedTypes: [], roomServices: [], documentTypes: [] }, creditCardCircuits: [], paymentMethods: [], customerTypes: [] });
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);
  const [sortState, setSortState] = useState({ key: "room", direction: "asc" });
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterPanelRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const editDataLoadRef = useRef(null);
  const [deleteBookingId, setDeleteBookingId] = useState("");

  const updateStayTabError = useCallback((tab, invalid) => {
    if (!["booking", "services", "checkout"].includes(tab)) return;
    setStayTabErrors((current) => current[tab] === Boolean(invalid) ? current : { ...current, [tab]: Boolean(invalid) });
  }, []);

  function ensureStayTabErrorBooking(booking) {
    const bookingId = String(booking?._id || "");
    if (stayTabErrorBookingIdRef.current === bookingId) return;
    stayTabErrorBookingIdRef.current = bookingId;
    setStayTabErrors({ booking: false, services: false, checkout: false });
  }

  useEffect(() => {
    const handleStayTabValidation = (event) => updateStayTabError(event?.detail?.tab, event?.detail?.invalid);
    document.addEventListener("vesta:stay-tab-validation", handleStayTabValidation);
    return () => document.removeEventListener("vesta:stay-tab-validation", handleStayTabValidation);
  }, [updateStayTabError]);

  async function loadCatalog() {
    const data = await getServiceCatalog();
    setCatalog(Array.isArray(data?.items) ? data.items : []);
  }

  function loadEditData() {
    if (editDataLoadRef.current) return editDataLoadRef.current;
    const request = Promise.all([
      getCustomers({}), getRooms(), getSites(), getCatalogItems("room-types", { includeInactive: true }), getCatalogItems("bed-types", { includeInactive: true }), getCatalogItems("room-services", { includeInactive: true }), getCatalogItems("document-types", { includeInactive: true }), getCreditCardCircuits(), getCatalogItems("payment-methods", { includeInactive: true }), getCatalogItems("customer-types", { includeInactive: true }),
    ]).then(([customersData, roomsData, sitesData, roomTypesData, bedTypesData, roomServicesData, documentTypesData, circuitsData, paymentMethodsData, customerTypesData]) => {
      const nextEditData = {
        customers: customersData?.items || [], rooms: roomsData?.items || [], sites: sitesData?.items || [],
        roomCatalogs: { roomTypes: roomTypesData?.items || [], bedTypes: bedTypesData?.items || [], roomServices: roomServicesData?.items || [], documentTypes: documentTypesData?.items || [] },
        creditCardCircuits: circuitsData?.items || [], paymentMethods: paymentMethodsData?.items || [], customerTypes: customerTypesData?.items || [],
      };
      setEditData(nextEditData);
      return nextEditData;
    }).catch((err) => {
      editDataLoadRef.current = null;
      throw err;
    });
    editDataLoadRef.current = request;
    return request;
  }

  async function loadAccounts(nextFilters = filters) {
    setLoading(true);
    setError("");
    try {
      const accountsData = await getBookingAccounts({ ...nextFilters, status: "CHECKED_IN" });
      setItems(Array.isArray(accountsData?.items) ? accountsData.items : []);
      if (!catalog.length) {
        loadCatalog().catch(() => setCatalog([]));
      }
    } catch (err) {
      setItems([]);
      setError(err?.response?.data?.error || "Impossibile caricare i conti prenotazione");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadAccounts(filters);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [filters.q, filters.from, filters.to]);

  useEffect(() => {
    loadEditData().catch(() => {});
    getFiscalDocumentTypes().then((data) => setFiscalDocumentTypes(Array.isArray(data?.items) ? data.items : [])).catch(() => setFiscalDocumentTypes([]));
  }, []);

  useEffect(() => {
    if (!activeFilter) return undefined;
    function closeFilters(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) setActiveFilter("");
    }
    document.addEventListener("pointerdown", closeFilters);
    return () => document.removeEventListener("pointerdown", closeFilters);
  }, [activeFilter]);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    function closeOptions(event) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) setOptionsOpen(false);
    }
    document.addEventListener("pointerdown", closeOptions);
    return () => document.removeEventListener("pointerdown", closeOptions);
  }, [optionsOpen]);

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  function resetSearchAndFilters() {
    setFilters({ q: "", from: "", to: "" });
    setColumnFilters(EMPTY_COLUMN_FILTERS);
    setSortState({ key: "room", direction: "asc" });
    setActiveFilter("");
    setOptionsOpen(false);
  }

  const filterOptions = useMemo(() => ({
    room: makeManagementFilterOptions(items, (item) => item.room_number || "-"),
    guest: makeManagementFilterOptions(items, (item) => item.guest_name || "-"),
    checkIn: makeManagementFilterOptions(items, (item) => formatDate(item.check_in)),
    checkOut: makeManagementFilterOptions(items, (item) => formatDate(item.check_out)),
    services: makeManagementFilterOptions(items, bookingServicesLabel),
    bookingTotal: makeManagementFilterOptions(items, (item) => formatMoney(item.booking_total)),
    servicesTotal: makeManagementFilterOptions(items, (item) => formatMoney(item.services_total)),
    invoiceTotal: makeManagementFilterOptions(items, (item) => formatMoney(item.invoice_total)),
  }), [items]);

  const filteredAccounts = useMemo(() => items.filter((item) => (
    matchesManagementFilter(item.room_number || "-", columnFilters.room)
    && matchesManagementFilter(item.guest_name || "-", columnFilters.guest, "text")
    && matchesManagementFilter(item.check_in, columnFilters.checkIn, "date")
    && matchesManagementFilter(item.check_out, columnFilters.checkOut, "date")
    && matchesManagementFilter(bookingServicesLabel(item), columnFilters.services)
    && matchesManagementFilter(item.booking_total, columnFilters.bookingTotal, "number")
    && matchesManagementFilter(item.services_total, columnFilters.servicesTotal, "number")
    && matchesManagementFilter(item.invoice_total, columnFilters.invoiceTotal, "number")
  )), [items, columnFilters]);

  const accountSortGetters = useMemo(() => ({
    room: { getter: (item) => item.room_number, type: "text" },
    guest: { getter: (item) => item.guest_name, type: "text" },
    checkIn: { getter: (item) => item.check_in, type: "date" },
    checkOut: { getter: (item) => item.check_out, type: "date" },
    services: { getter: bookingServicesLabel, type: "text" },
    bookingTotal: { getter: (item) => item.booking_total, type: "number" },
    servicesTotal: { getter: (item) => item.services_total, type: "number" },
    invoiceTotal: { getter: (item) => item.invoice_total, type: "number" },
  }), []);
  const orderedAccounts = useMemo(() => sortManagementRows(filteredAccounts, sortState, accountSortGetters), [filteredAccounts, sortState, accountSortGetters]);

  function toggleFilter(columnKey) { setActiveFilter((current) => (current === columnKey ? "" : columnKey)); }
  function updateCheckFilter(columnKey, value, checked) {
    setColumnFilters((current) => {
      const values = Array.isArray(current[columnKey]) ? current[columnKey] : [];
      return { ...current, [columnKey]: checked ? [...values, value] : values.filter((item) => item !== value) };
    });
  }
  function clearColumnFilter(columnKey) { setColumnFilters((current) => ({ ...current, [columnKey]: [] })); }
  function updateRangeFilter(columnKey, value) { setColumnFilters((current) => ({ ...current, [columnKey]: value })); }
  function toggleSort(columnKey) { setSortState((current) => ({ key: columnKey, direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc" })); }

  const filterProps = { activeFilter, onToggle: toggleFilter, onCheckChange: updateCheckFilter, onClear: clearColumnFilter, onRangeChange: updateRangeFilter, onSort: toggleSort };
  const incrementalRows = useIncrementalRows(orderedAccounts);

  function openServiceOverlay(booking) {
    ensureStayTabErrorBooking(booking);
    setActiveStayTab("services");
    setSelectedBooking(booking);
    setCheckoutBooking((current) => String(current?._id || "") === String(booking._id || "") ? current : booking);
    setCheckoutDraft((current) => String(checkoutBooking?._id || "") === String(booking._id || "") ? current : buildCheckoutDraft(booking));
    setOverlayError("");
    setServiceDraft(buildServiceDraftFromBooking(booking));
  }

  function updateServiceDraft(patch) {
    setServiceDraft((current) => {
      const next = { ...current, ...patch };
      setSelectedBooking((booking) => booking?._id ? mergeServiceDraftIntoBookingAccount(booking, next) : booking);
      setCheckoutBooking((booking) => booking?._id ? mergeServiceDraftIntoBookingAccount(booking, next) : booking);
      return next;
    });
  }

  async function openCheckoutOverlay(booking) {
    ensureStayTabErrorBooking(booking);
    setCheckoutError("");
    try {
      const [account] = await Promise.all([fetchFreshBookingAccount(booking), loadEditData()]);
      setCheckoutBooking(account);
      setCheckoutDraft(buildCheckoutDraft(account));
      setActiveStayTab("checkout");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Dati check out non disponibili");
    }
  }

  async function updateServiceBookingDate(field, value) {
    if (!selectedBooking?._id || !value) return;
    const previous = selectedBooking;
    const nextBooking = { ...selectedBooking, [field]: value };
    const nights = stayNights(nextBooking.check_in, nextBooking.check_out);
    if (nights <= 0) {
      setOverlayError("Check Out deve essere successivo al Check In");
      return;
    }
    setOverlayError("");
    setSelectedBooking(nextBooking);
    setCheckoutBooking((current) => String(current?._id || "") === String(nextBooking._id || "") ? { ...current, [field]: value } : current);
    setEditDraft((current) => current ? { ...current, [field]: value } : current);
    setServiceDraft((current) => ({ ...current, stay: { ...(current.stay || {}), quantity: nights } }));
    try {
      await updateBooking(selectedBooking._id, { [field]: value });
      setItems((current) => current.map((item) => String(item._id) === String(selectedBooking._id) ? { ...item, [field]: value, stay_nights: nights } : item));
    } catch (err) {
      setSelectedBooking(previous);
      setServiceDraft((current) => ({ ...current, stay: { ...(current.stay || {}), quantity: stayNights(previous.check_in, previous.check_out) } }));
      setOverlayError(err?.response?.data?.error || "Impossibile aggiornare le date prenotazione");
    }
  }

  const updateCheckoutDraft = useCallback((patch) => {
    setCheckoutDraft((current) => ({ ...current, ...patch }));
    if (Object.prototype.hasOwnProperty.call(patch || {}, "payments")) {
      const payments = normalizeBookingPayments(patch.payments, 0, "", true);
      setCheckoutBooking((current) => current ? { ...current, payments } : current);
      setSelectedBooking((current) => current ? { ...current, payments } : current);
      setEditDraft((current) => current ? { ...current, payments } : current);
    }
  }, []);

  async function saveCheckoutDocument() {
    updateStayTabError("checkout", false);
    setCheckoutError("");
    if (!checkoutBooking?._id) { setCheckoutError("Prenotazione non valida"); return false; }
    if (!checkoutDraft.emissionDate) { setCheckoutError("Data Emissione obbligatoria"); return false; }
    if (!/^\d+$/.test(String(checkoutDraft.documentNumber || "")) || Number(checkoutDraft.documentNumber) <= 0) {
      setCheckoutError("Numero Documento obbligatorio e composto solo da numeri"); return false;
    }
    const payments = normalizeBookingPayments(checkoutDraft.payments);
    if (payments.some((payment) => !String(payment.paymentMethod || "").trim() || !Number.isFinite(Number(payment.amount)) || Number(payment.amount) < 0)) {
      setCheckoutError("Completa i Dati Pagamenti"); return false;
    }
    if (payments.some((payment) => payment.paymentType === "ADVANCE" && Number(payment.amount || 0) > 0 && payment?.advanceDocument?.issued !== true)) {
      setCheckoutError("Emetti il documento fiscale per tutti gli Acconti prima di concludere il soggiorno"); return false;
    }
    setCheckoutSaving(true);
    try {
      if (selectedBooking?._id && String(selectedBooking._id) === String(checkoutBooking._id) && serviceDraft?.stay) {
        await persistServiceDraft();
      }
      if (editBooking?._id && String(editBooking._id) === String(checkoutBooking._id) && editDraft) {
        const editPayments = normalizeBookingPayments(checkoutDraft.payments);
        const { total_amount: _ignoredTotalAmount, room_rate: _ignoredRoomRate, room_discount_rate: _ignoredRoomDiscountRate, room_vat_rate: _ignoredRoomVatRate, room_charge_date: _ignoredRoomChargeDate, room_note: _ignoredRoomNote, ...bookingDraftForSave } = editDraft;
        await updateBooking(editBooking._id, { ...bookingDraftForSave, payments: editPayments, deposit_amount: editPayments.filter((payment) => ["ADVANCE", "DEPOSIT"].includes(payment.paymentType)).reduce((sum, payment) => sum + Number(payment.amount || 0), 0) });
      }
      const result = await issueBookingCheckoutDocument(checkoutBooking._id, {
        documentId: checkoutDraft.documentId || undefined,
        documentType: checkoutDraft.documentType,
        numberingMode: checkoutDraft.numberingMode,
        documentNumber: checkoutDraft.documentNumber,
        emissionDate: checkoutDraft.emissionDate,
        orientation: "PORTRAIT",
        unconditionalDiscountAmount: checkoutDraft.unconditionalDiscountAmount,
        unconditionalDiscountRate: checkoutDraft.unconditionalDiscountRate,
        roundingAmount: checkoutDraft.roundingAmount,
        payments,
        note: checkoutDraft.note,
      });
      const issued = result?.item || {};
      const issuedPayments = Array.isArray(result?.payments) ? normalizeBookingPayments(result.payments) : payments;
      const issuedBookingPatch = { status: "CHECKED_OUT", latest_fiscal_document: issued, payments: issuedPayments };
      setCheckoutBooking((current) => ({ ...(current || checkoutBooking), ...issuedBookingPatch }));
      setSelectedBooking((current) => current ? { ...current, ...issuedBookingPatch } : current);
      setEditBooking((current) => current ? { ...current, ...issuedBookingPatch } : current);
      setEditDraft((current) => current ? { ...current, status: "CHECKED_OUT", _originalStatus: "CHECKED_OUT", payments: issuedPayments } : current);
      setCheckoutDraft((current) => ({
        ...current,
        documentId: issued._id || current.documentId,
        documentNumber: String(issued.documentSequence || issued.documentNumber || current.documentNumber || "").replace(/\D/g, "") || current.documentNumber,
        payments: issuedPayments,
      }));
      await loadAccounts(filters);
      return true;
    } catch (err) {
      setCheckoutError(err?.response?.data?.error || "Errore emissione documento");
      return false;
    } finally {
      setCheckoutSaving(false);
    }
  }

  async function persistServiceDraft() {
    setOverlayError("");
    if (!selectedBooking?._id) return null;
    const rows = Array.isArray(serviceDraft?.rows) ? serviceDraft.rows : [];
    for (const row of rows) {
      if (!row.service_id || !row.chargeDate) throw new Error("Completa i Dati Richiesti");
      if (numberValue(row.quantity, 0) <= 0 || numberValue(row.unitPrice, -1) < 0) throw new Error("Dati Errati");
      if (numberValue(row.discountRate, -1) < 0 || numberValue(row.discountRate, 101) > 100) throw new Error("Sconto non valido");
      if (numberValue(row.vatRate, -1) < 0 || numberValue(row.vatRate, 101) > 100) throw new Error("Aliquota IVA non valida");
    }

    const stay = serviceDraft?.stay || {};
    const nights = Math.max(1, stayNights(selectedBooking.check_in, selectedBooking.check_out));
    const roomRate = Math.max(0, numberValue(stay.unitPrice, 0));
    const roomDiscountRate = Math.min(100, Math.max(0, numberValue(stay.discountRate, 0)));
    const bookingTotal = nights * roomRate * (1 - roomDiscountRate / 100);
    await updateBooking(selectedBooking._id, {
      room_rate: roomRate,
      room_discount_rate: roomDiscountRate,
      room_vat_rate: Math.min(100, Math.max(0, numberValue(stay.vatRate, 0))),
      room_charge_date: stay.chargeDate || selectedBooking.check_in,
      room_note: stay.note || "",
      total_amount: bookingTotal,
    });
    for (const serviceId of (serviceDraft.removedIds || [])) await deleteBookingAccountService(selectedBooking._id, serviceId);
    for (const row of rows) {
      const payload = {
        service_id: row.service_id,
        description: row.description,
        chargeType: row.chargeType,
        entrySource: row.entrySource || "MANUAL_DESKTOP",
        accumulationItems: Array.isArray(row.accumulationItems) ? row.accumulationItems : [],
        quantity: row.quantity,
        unitPrice: row.unitPrice,
        discountRate: row.discountRate,
        vatRate: row.vatRate,
        chargeDate: row.chargeDate,
        note: row.note,
      };
      if (row._id) await updateBookingAccountService(selectedBooking._id, row._id, payload);
      else await createBookingAccountService(selectedBooking._id, payload);
    }
    const fresh = await fetchFreshBookingAccount({ ...selectedBooking, status: "CHECKED_IN" });
    const liveFresh = { ...fresh, payments: normalizeBookingPayments(checkoutDraft.payments) };
    setSelectedBooking(liveFresh);
    setCheckoutBooking((current) => String(current?._id || "") === String(liveFresh._id || "") ? { ...liveFresh, latest_fiscal_document: current?.latest_fiscal_document || liveFresh.latest_fiscal_document, latest_reversal_document: current?.latest_reversal_document || liveFresh.latest_reversal_document } : current);
    setServiceDraft(buildServiceDraftFromBooking(liveFresh));
    return liveFresh;
  }

  async function saveService() {
    updateStayTabError("services", false);
    try {
      await persistServiceDraft();
      closeStayOverlay();
      await loadAccounts(filters);
    } catch (err) {
      setOverlayError(err?.response?.data?.error || err?.message || "Errore salvataggio servizi");
    }
  }

  async function removeStayMovement(booking) {
    if (!booking?._id) return;
    if (String(booking.status || "").toUpperCase() === "CHECKED_OUT") return;
    if (deleteBookingId !== String(booking._id)) {
      setDeleteBookingId(String(booking._id));
      return;
    }
    setError("");
    try {
      await deleteBooking(booking._id);
      setDeleteBookingId("");
      closeStayOverlay();
      await loadAccounts(filters);
    } catch (err) {
      setDeleteBookingId("");
      setError(err?.response?.data?.error || "Impossibile eliminare il soggiorno e i movimenti collegati");
    }
  }

  function openBookingEdit(booking) {
    ensureStayTabErrorBooking(booking);
    const sameCheckout = String(checkoutBooking?._id || "") === String(booking?._id || "");
    const sourceBooking = sameCheckout ? checkoutBooking : booking;
    setActiveStayTab("booking");
    setEditError("");
    setEditBooking(sourceBooking);
    setEditDraft(bookingToDraft(sourceBooking));
    setCheckoutBooking(sourceBooking);
    if (!sameCheckout) setCheckoutDraft(buildCheckoutDraft(sourceBooking));
    loadEditData().catch((err) => {
      setEditError(err?.response?.data?.error || "Impossibile caricare i dati di supporto della prenotazione");
    });
  }

  function closeStayOverlay() {
    setSelectedBooking(null);
    setCheckoutBooking(null);
    setEditBooking(null);
    setEditDraft(null);
    setServiceDraft({ stay: null, rows: [], removedIds: [] });
    setCheckoutDraft(EMPTY_CHECKOUT_DRAFT);
    setOverlayError("");
    setCheckoutError("");
    setEditError("");
    stayTabErrorBookingIdRef.current = "";
    setStayTabErrors({ booking: false, services: false, checkout: false });
  }

  async function changeStayTab(tab, booking) {
    const currentBooking = booking || editBooking || selectedBooking || checkoutBooking;
    if (!currentBooking?._id || tab === activeStayTab) return;
    const sameId = (value) => String(value?._id || "") === String(currentBooking._id || "");
    if (tab === "booking") {
      if (sameId(editBooking) && editDraft) setActiveStayTab("booking");
      else openBookingEdit(currentBooking);
      return;
    }
    if (tab === "services") {
      if (sameId(selectedBooking) && serviceDraft?.stay) setActiveStayTab("services");
      else openServiceOverlay(currentBooking);
      return;
    }
    if (tab === "checkout") {
      if (sameId(checkoutBooking)) setActiveStayTab("checkout");
      else await openCheckoutOverlay(currentBooking);
    }
  }

  function updateEditDraft(field, value) {
    setEditDraft((current) => ({ ...(current || {}), [field]: value }));
    if (field === "payments") {
      const payments = normalizeBookingPayments(value, 0, "", true);
      setCheckoutDraft((current) => ({ ...current, payments }));
      setCheckoutBooking((current) => current ? { ...current, payments } : current);
      setSelectedBooking((current) => current ? { ...current, payments } : current);
      return;
    }
    if (["check_in", "check_out", "room_number", "guest_name", "guest_email", "guest_phone", "payment_method"].includes(field)) {
      setCheckoutBooking((current) => current ? { ...current, [field]: value } : current);
      setSelectedBooking((current) => current ? { ...current, [field]: value } : current);
    }
  }

  function selectEditCustomer(target, customer) {
    const id = String(customer?._id || customer?.id || "");
    const name = String(customer?.businessName || customer?.ragioneSociale || [customer?.surname || customer?.cognome, customer?.name || customer?.nome].filter(Boolean).join(" ") || "").trim();
    const contacts = Array.isArray(customer?.contacts) ? customer.contacts : [];
    const email = contacts.find((row) => ["email", "pec"].includes(String(row?.contactType || row?.tipoContatto || "").toLowerCase()))?.contactValue || customer?.email || customer?.pec || "";
    const phone = contacts.find((row) => ["telefono", "cellulare"].includes(String(row?.contactType || row?.tipoContatto || "").toLowerCase()))?.contactValue || customer?.phone || customer?.telefono || customer?.cellulare || "";
    setEditDraft((current) => target === "invoice" ? { ...(current || {}), invoice_customer_id: id, invoice_name: name, invoice_email: email, invoice_phone: phone } : { ...(current || {}), customer_id: id, guest_name: name, guest_email: email, guest_phone: phone, ...(current?.invoice_same_as_customer !== false ? { invoice_customer_id: id, invoice_name: name, invoice_email: email, invoice_phone: phone } : {}) });
  }

  async function saveBookingEdit() {
    if (!editBooking?._id || !editDraft) return;
    updateStayTabError("booking", false);
    setEditError("");
    if (!String(editDraft.guest_name || "").trim() || !editDraft.room_number || !editDraft.check_in || !editDraft.check_out || !editDraft.check_in_time || !editDraft.check_out_time) return setEditError("Completa i Dati Richiesti");
    const editCheckIn = toCheckInDateTimeFromInput(editDraft.check_in, editDraft.check_in_time);
    const editCheckOut = toCheckOutDateTimeFromInput(editDraft.check_out, editDraft.check_out_time);
    if (!editCheckIn || !editCheckOut || new Date(editCheckOut) <= new Date(editCheckIn)) return setEditError("Dati Errati");
    try {
      const payments = Array.isArray(editDraft.payments) ? editDraft.payments : [];
      const depositAmount = payments.filter((row) => ["ADVANCE", "DEPOSIT"].includes(String(row?.paymentType || (row?.isDeposit === true ? "ADVANCE" : "PAYMENT")).toUpperCase())).reduce((sum, row) => sum + Number(row?.amount || 0), 0);
      await updateBooking(editBooking._id, {
        ...editDraft,
        guest_name: String(editDraft.guest_name || "").trim(),
        guest_email: String(editDraft.guest_email || "").trim(),
        guest_phone: String(editDraft.guest_phone || "").trim(),
        invoice_customer_id: editDraft.invoice_same_as_customer !== false ? String(editDraft.customer_id || "") : String(editDraft.invoice_customer_id || ""),
        invoice_name: editDraft.invoice_same_as_customer !== false ? String(editDraft.guest_name || "").trim() : String(editDraft.invoice_name || "").trim(),
        invoice_email: editDraft.invoice_same_as_customer !== false ? String(editDraft.guest_email || "").trim() : String(editDraft.invoice_email || "").trim(),
        invoice_phone: editDraft.invoice_same_as_customer !== false ? String(editDraft.guest_phone || "").trim() : String(editDraft.invoice_phone || "").trim(),
        guests: Array.isArray(editDraft.relatedGuests) ? editDraft.relatedGuests : [],
        payments,
        deposit_amount: depositAmount,
        total_amount: Number(editDraft.total_amount || 0),
        check_in: editCheckIn,
        check_out: editCheckOut,
      });
      closeStayOverlay();
      await loadAccounts(filters);
    } catch (err) {
      setEditError(err?.response?.data?.error || "Errore salvataggio prenotazione");
    }
  }

  return (
    <section className="window_page bookings_page management_page management_shared_page booking_accounts_page">
      <div className="window_banner">
        <div className="window_banner_copy">
          <h1>SOGGIORNI</h1>
          <p>Ricerca le prenotazioni attive, controlla i servizi caricati sul soggiorno e verifica il totale fattura finale.</p>
        </div>
      </div>

      <div className="management_list_panel booking_accounts_panel">
        <div className={`booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_shared_toolbar booking_accounts_toolbar${filtersExpanded ? " is_filters_expanded" : ""}`}>
          <BookingAccountOptionsMenu
            open={optionsOpen}
            menuRef={optionsMenuRef}
            onToggle={() => setOptionsOpen((current) => !current)}
            onReset={resetSearchAndFilters}
          />
          <input className="management_search" value={filters.q} placeholder="Cerca soggiorni (es. Mario, Rossi)" onChange={(event) => updateFilter("q", formatManagementSearchInput(event.target.value))} aria-label="Ricerca descrittiva fatturazione" />
          <button
            type="button"
            className="vesta_filters_toggle booking_accounts_filters_toggle"
            onClick={() => setFiltersExpanded((current) => !current)}
            aria-expanded={filtersExpanded}
            aria-label={filtersExpanded ? "Accorcia filtri fatturazione" : "Espandi filtri fatturazione"}
            title={filtersExpanded ? "Accorcia filtri" : "Espandi filtri"}
          >
            <span className="vesta_filters_toggle_icon" aria-hidden="true"><span></span><span></span></span>
          </button>
          <div className="management_toolbar_results"><strong className="management_results_count">Risultati: {filteredAccounts.length}</strong></div>
          <div className="booking_accounts_extra_filters" aria-hidden={!filtersExpanded}>
            <label className="management_inline_filter">
              <span>Dal Giorno</span>
              <VestaDateInput value={filters.from} outputFormat="iso" onChange={(value) => updateFilter("from", value)} />
            </label>
            <label className="management_inline_filter">
              <span>Al Giorno</span>
              <VestaDateInput value={filters.to} outputFormat="iso" onChange={(value) => updateFilter("to", value)} />
            </label>
          </div>
        </div>

        <div className="management_board management_table_board booking_accounts_board">
          <div className="management_table_shell" role="table" aria-label="Elenco conti prenotazioni" ref={incrementalRows.shellRef} onScroll={incrementalRows.handleScroll}>
            <div className="management_table_header management_shared_table_header booking_accounts_table_header" role="row" ref={filterPanelRef}>
              <ManagementFilterHeader columnKey="room" label="Camera" options={filterOptions.room} value={columnFilters.room} sortDirection={sortState.key === "room" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="guest" label="Cliente" value={columnFilters.guest} filterType="text" sortDirection={sortState.key === "guest" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="checkIn" label="Check In" value={columnFilters.checkIn} filterType="date" sortDirection={sortState.key === "checkIn" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="checkOut" label="Check Out" value={columnFilters.checkOut} filterType="date" sortDirection={sortState.key === "checkOut" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="services" label="Servizi Addebitati" options={filterOptions.services} value={columnFilters.services} sortDirection={sortState.key === "services" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="bookingTotal" label="Totale Camera" value={columnFilters.bookingTotal} filterType="number" sortDirection={sortState.key === "bookingTotal" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="servicesTotal" label="Totale Servizi" value={columnFilters.servicesTotal} filterType="number" sortDirection={sortState.key === "servicesTotal" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="invoiceTotal" label="Totale Fattura" value={columnFilters.invoiceTotal} filterType="number" sortDirection={sortState.key === "invoiceTotal" ? sortState.direction : ""} {...filterProps} />
              <div className="management_table_actions_head" role="columnheader">Azioni</div>
            </div>
            {loading && <ManagementListLoading label="Caricamento Conti" />}
            {!loading && error && <div className="management_table_visible_status booking_accounts_status is_error">{error}</div>}
            {!loading && !error && filteredAccounts.length === 0 && <div className="management_table_visible_status booking_accounts_status">Nessun Risultato Trovato</div>}
            <div className="management_table_body" role="rowgroup">
              {incrementalRows.rows.map((item) => (
                <article key={item._id} className="management_table_row booking_accounts_table_row" data-vesta-focus-id={item._id} role="row">
                  <div className="management_table_cell management_table_title_cell" role="cell"><strong>{item.room_number || "-"}</strong></div>
                  <div className="management_table_cell" role="cell"><strong>{item.guest_name || "-"}</strong></div>
                  <div className="management_table_cell" role="cell">{formatDateTime(item.check_in)}</div>
                  <div className="management_table_cell" role="cell">{formatDateTime(item.check_out)}</div>
                  <div className="management_table_cell" role="cell">
                    <div className="booking_accounts_services_cell">
                      {bookingServiceDisplayRows(item).map((service) => (
                        <div key={service.key} className="booking_accounts_service_summary_line">
                          <strong>{service.description}</strong>
                          <em>{formatMoney(service.grossAmount)}</em>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="management_table_cell" role="cell">{formatMoney(item.booking_total)}</div>
                  <div className="management_table_cell" role="cell">{formatMoney(item.services_total)}</div>
                  <div className="management_table_cell" role="cell">
                    <div className="booking_accounts_total_cell">
                      <strong>{formatMoney(item.invoice_total)}</strong>
                      <span>Saldo {formatMoney(item.balance_due)}</span>
                    </div>
                  </div>
                  <div className="management_table_actions management_status_actions" role="cell">
                    {String(item.status || "").toUpperCase() === "CHECKED_OUT" ? (
                      <>
                        <button type="button" className="management_action_button management_action_primary" onClick={() => openServiceOverlay(item)}>Modifica</button>
                        <span className="booking_disabled_delete_hint" data-tooltip="Non puoi eliminare la prenotazione perché è fatturata.">
                          <button type="button" className="management_action_button management_action_danger" disabled>Elimina</button>
                        </span>
                      </>
                    ) : deleteBookingId === String(item._id) ? (
                      <>
                        <button type="button" className="management_action_button" onClick={() => setDeleteBookingId("")}>Annulla</button>
                        <button type="button" className="management_action_button management_action_danger" onClick={() => removeStayMovement(item)}>Elimina</button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="management_action_button management_action_primary" onClick={() => openServiceOverlay(item)}>Modifica</button>
                        <button type="button" className="management_action_button management_action_danger" onClick={() => removeStayMovement(item)}>Elimina</button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>

      {activeStayTab === "services" && selectedBooking && (
        <BookingServiceOverlay
          booking={selectedBooking}
          catalog={catalog}
          draft={serviceDraft}
          error={overlayError}
          onChange={updateServiceDraft}
          onBookingDateChange={updateServiceBookingDate}
          onClose={closeStayOverlay}
          onSave={saveService}
          activeStayTab={activeStayTab}
          onStayTabChange={(tab) => changeStayTab(tab, selectedBooking)}
          stayTabErrors={stayTabErrors}
          onStayTabErrorChange={updateStayTabError}
          locked={String(selectedBooking.status || "").toUpperCase() === "CHECKED_OUT"}
        />
      )}

      {activeStayTab === "booking" && editBooking && editDraft && (
        <BookingOverlay
          mode="edit" draft={editDraft} error={editError} onChange={updateEditDraft}
          onClose={closeStayOverlay} onSave={saveBookingEdit}
          onCustomerAction={() => {}} onCustomerSelect={selectEditCustomer} onRoomAction={() => {}}
          roomCatalogs={editData.roomCatalogs} rooms={editData.rooms} sites={editData.sites}
          creditCardCircuits={editData.creditCardCircuits} paymentMethodCatalog={editData.paymentMethods}
          customers={editData.customers} customerTypeCatalog={editData.customerTypes}
          bookingId={editBooking._id} activeStayTab={activeStayTab} onStayTabChange={(tab) => changeStayTab(tab, editBooking)}
          stayTabErrors={stayTabErrors} onStayTabErrorChange={updateStayTabError}
          locked={String(editBooking.status || "").toUpperCase() === "CHECKED_OUT"}
        />
      )}

      {activeStayTab === "checkout" && checkoutBooking && (
        <BookingCheckoutOverlay
          booking={checkoutBooking}
          draft={checkoutDraft}
          error={checkoutError}
          saving={checkoutSaving}
          paymentMethodCatalog={editData.paymentMethods}
          fiscalDocumentTypes={fiscalDocumentTypes}
          onChange={updateCheckoutDraft}
          onClose={closeStayOverlay}
          onSave={saveCheckoutDocument}
          onSaveAndClose={async () => { if (await saveCheckoutDocument()) closeStayOverlay(); }}
          onBookingStateChange={(patch) => {
            setCheckoutBooking((current) => current ? { ...current, ...patch } : current);
            setSelectedBooking((current) => current ? { ...current, ...patch } : current);
            setEditBooking((current) => current ? { ...current, ...patch } : current);
            if (patch?.status) setEditDraft((current) => current ? { ...current, status: patch.status, _originalStatus: patch.status } : current);
            if (patch?.status === "CHECKED_IN") loadAccounts(filters).catch(() => {});
          }}
          activeStayTab={activeStayTab}
          onStayTabChange={(tab) => changeStayTab(tab, checkoutBooking)}
          stayTabErrors={stayTabErrors}
          onStayTabErrorChange={updateStayTabError}
        />
      )}
    </section>
  );
}
